import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { format, formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { useProjectHubScopedData } from '../../hooks/useProjectHubProjectData';
import {
  subscribeArchitectUnversionedProjectFiles,
  subscribeProjectFiles,
  subscribeProjectFinancialFiles,
  subscribeProjectFileStreams,
  subscribeProjectFileVersionsForStream,
  stampStreamInheritVersionsAfter,
  deleteProjectFile,
  deleteProjectFinancialFile,
  deleteProjectFileStream,
  renameProjectFileStream,
  renameProjectFileStructureGroup,
  updateProjectFileVersionEdit,
  updateProjectFileStreamStructures,
  moveUnversionedProjectFile,
  moveProjectFileVersionsToStream,
  assignProjectFileToStructureGroups,
  type MoveVersionsTarget,
  listCncProjectFiles,
  isVersionTrackWithNoFiles,
  countUnreadCommentsOnProjectFile,
  findProjectFileForDeepLink,
  resolveAnnotationViewerOpenTarget,
  isCsvType,
  isImageType,
  isPdfType,
  isHtmlType,
} from '../../utils/projectFileService';
import {
  buildHubStructureOptions,
  streamStructureNameSyncPatches,
} from '../../utils/projectHubStructureOptions';
import {
  subscribeProjectHubReleases,
  subscribeProjectVendorReleases,
  subscribeRfqPackagesForDeal,
  subscribeRfqProjectApprovalsForDeal,
  subscribeRfqSubmissionsForDeal,
  resolveSupplierOrgNames,
  type RfqProjectApprovalFields,
} from '../../utils/projectRfqFiles';
import { subscribeProjectSubmittals } from '../../utils/projectSubmittals';
import { subscribeDesignerStandards } from '../../utils/designerStandardsService';
import {
  PROJECT_FILES_TOUR_EVENT,
  resolveProjectFilesTourScript,
  type ProjectFilesTourEventDetail,
} from '../../utils/projectFilesTourSteps';
import { useProjectFilesTour } from './ProjectFilesTour';
import FilesHelpButton from './FilesHelpButton';
import {
  anyStreamTagged,
  groupKeyForStream,
  groupStreamsByStructure,
  streamDisplayTitle,
  structureNamePrefixLabels,
  titleWithPreservedPrefix,
  WHOLE_PROJECT_GROUP_LABEL,
  summarizeStreamStatus,
  WHOLE_PROJECT_GROUP_ID,
  type StreamStructureGroup,
} from '../../utils/projectFileStreamGroups';
import { structureGroupIdsForFile } from '../../utils/projectFileGroupAssignment';
import { structureSelectionLabel } from '../../utils/structureSelectionLabel';
import { compareStreamsByRailOrder } from '../../utils/projectFileStructureTracks';
import {
  headVersionForStream,
  isInheritedVersion,
  sharedStructureBadge,
  versionsForStream,
  effectiveInheritVersionsAfter,
  withSharedStreamVersions,
} from '../../utils/projectFileStreamVersions';
import ProjectSubmittalsView from './ProjectSubmittalsView';
import ProjectRfqFilesView from './ProjectRfqFilesView';
import ProjectFoldersView from './ProjectFoldersView';
import './ProjectFilesViews.css';
import { resolveFileViewerLeadId } from '../../utils/projectFileViewerLeadId';
import { subscribeToAnnotationsForProject, pdfAnnotationHasUnreadForUser } from '../../utils/pdfAnnotationService';
import { db } from '../../utils/firebase';
import {
  findPortalIdForDeal,
  isReleasableStreamKind,
  subscribeStreamReleases,
  syncPortalApprovalTrailGrouping,
  unreleaseProjectFileFromPortal,
  type ReleasableStreamKind,
} from '../../utils/portalFileReleaseService';
import {
  canAccessProjectFilesRfqsView,
  canAccessProjectHubClientPortalControls,
  canManageProjectFileStructures,
  canEditProjectUploadVersionLabel,
  canManagePlanReview,
  canReadPortalFileReleaseCollectionGroup,
  canSeeProjectFinancials,
} from '../../utils/userRoles';
import { projectFoldersForViewer } from '../../utils/projectFileFolders';
import { isSupplierHubVisibleStreamKind, SUPPLIER_HUB_FILE_STREAM_KINDS } from '../../utils/supplierHubFiles';
import {
  ARCHITECT_HUB_FILE_STREAM_KINDS,
  isArchitectHubVisibleStreamKind,
  isArchitectHubVisibleUnversionedFile,
} from '../../utils/architectHubFiles';
import {
  startPlanReview,
  subscribePlanReviewsForStream,
} from '../../utils/planReviewService';
import { buildVersionPlanReviewMap } from '../../utils/planReviewSummary';
import { postPlanReviewDesignGroupComment } from '../../utils/planReviewDesignComment';
import type {
  DesignerStandardItem,
  EnhancedDeal,
  PdfAnnotation,
  PlanReview,
  PortalFileRelease,
  ProjectFile,
  ProjectFileStream,
  ProjectFileStreamKind,
  RfqHubFileRelease,
  RfqPackage,
  RfqRequest,
  RfqSubmission,
  ProposalNodeSet,
  VendorFileRelease,
} from '../../types';
import {
  FileText, Upload, Trash2, Loader2,
  FolderOpen, Folder as FolderIcon,
  ChevronRight, Layers, Tag,
  Send, CheckCircle2, RotateCw, Undo2, ClipboardCheck,
  Truck, Users, GitFork, Share2, Pencil, MoveRight, Plus,
} from 'lucide-react';
import MoveVersionsModal from './MoveVersionsModal';
import MoveStructureGroupModal from './MoveStructureGroupModal';
import StructureSetupWizard from './StructureSetupWizard';
import AddStructureModal from './AddStructureModal';
import { addProjectStructureAfterSplit, type AddStructureDestination } from '../../utils/addProjectStructure';
import {
  timberListTracksForStructureSetup,
  type StructureFromTimberListRow,
} from '../../utils/proposalStructuresFromTimberLists';
import {
  setUpProjectStructures,
  splitCandidateTracks,
  type ProjectStructureSetupPlan,
} from '../../utils/projectStructureSetup';
import './SplitTracksModal.css';
import './CreateStructuresFromTimberListsModal.css';
import './ProjectFiles.css';
import { useProjectUploadModal } from './ProjectUploadModalProvider';
import { trackKindShort } from './projectUploadModalShared';
import {
  groupProjectFileVersionsByStage,
  PROJECT_FILE_DETAIL_STAGE_LABELS,
  PROJECT_FILE_DETAIL_STAGES,
  type ProjectFileDetailStage,
} from '../../utils/projectFileVersionGroups';
import { subscribeProposalStructures, type StructureRef } from '../../utils/proposalStructureApi';
import { STRUCTURES_LOCKED_HINT, structuresUnlocked } from '../../utils/projectStructureGate';
import StructureCheckDropdown from './StructureCheckDropdown';
import SplitHistoryPopover from './SplitHistoryPopover';
import SplitAnnouncement from './SplitAnnouncement';
import { buildSplitHistory } from '../../utils/projectFileSplitHistory';
import {
  pendingSplitAnnouncement,
  readSplitSeenAt,
  writeSplitSeenAt,
} from '../../utils/projectFileSplitAnnouncement';
import { useFilesRedesignAccess } from '../../utils/filesRedesignAccess';
import { useRfqRequestsAccess } from '../../utils/rfqRequestsAccess';
import { sendRfqRequest, subscribeRfqRequestsForDeal } from '../../utils/rfqRequests';
import { buildRfqCoverage, countOpenRequests } from '../../utils/rfqRequestCoverage';
import { selectRfqProjectForDeal } from '../../utils/vendorAdminProjectSummary';
import { projectSiteInputFromProjectAndDeal } from '../../utils/projectAddressService';
import type { ProposalSpeciesSelectionSource } from '../../utils/timberRfqStructureScope';
import ProjectRfqRequestsView from './ProjectRfqRequestsView';
import RequestTimberQuotesWizard, { type RequestTimberQuotesDraft } from './RequestTimberQuotesWizard';
import { cncStructureConflicts, defaultCncStructureIdsForTrack } from '../../utils/cncModelStructures';
import { portalHostedAssetHref, portalHtmlPreviewHref } from '../../utils/portalAssetUrl';
import { displayNameFromFileRef, parseProjectFilesStoragePath } from '../../utils/projectFileRef';
import ProjectFilesUnversioned from './ProjectFilesUnversioned';
import ProjectFileCommentsButton from './ProjectFileCommentsButton';
import ReleaseToClientPortalModal from './ReleaseToClientPortalModal';
import ReleaseToVendorModal from '../vendor/ReleaseToVendorModal';
import ReleaseToRfqStagingModal from '../vendor/ReleaseToRfqStagingModal';
import { isVendorReleasableStreamKind } from '../../utils/vendorFileReleaseService';
import {
  isRfqStagingReleasableStreamKind,
  subscribeProjectStreamHubReleases,
  type RfqStagingReleasableStreamKind,
} from '../../utils/rfqHubFileReleaseService';
import { formatProposalLinkBadge, proposalRfqLinkFromRelease } from '../../utils/proposalRfqVersionLink';
import {
  emptyPortalApprovalTargetGroup,
  findClientApprovalForFileReleases,
  formatApprovalBadge,
  groupPortalDealApprovals,
  type PortalApprovalTargetGroup,
} from '../../utils/portalDeliverableApprovalService';
import { parsePortalProjectRow } from '../../utils/portalProjectView';
import type { PdfViewerSiblingVersion } from '../pdf/PdfViewerModal';
import type { HtmlViewerSibling } from '../html/HtmlViewerModal';

const PdfViewerModal = lazy(() => import('../pdf/PdfViewerModal'));
const ImageViewerModal = lazy(() => import('../pdf/ImageViewerModal'));
const CsvViewerModal = lazy(() => import('../csv/CsvViewerModal'));
const HtmlViewerModal = lazy(() => import('../html/HtmlViewerModal'));

export interface ProjectFilesDeepLink {
  documentId: string;
  annotationId?: string;
  pageNumber?: number;
}

/** Files tab views. Partners see a subset (suppliers: deliverables only; architects: + folders). */
export type ProjectFilesView = 'deliverables' | 'submittals' | 'rfqs' | 'folders';

const CLASSIC_VIEW_KEY = 'pf-classic-view';
const FILES_VIEW_KEY = (projectId: string) => `pf:${projectId}:view`;

/** How a stream's versions are laid out: grouped by level of detail, or one flat historical timeline. */
type VersionLayout = 'lod' | 'timeline';
/** Remembered separately per mode — the new view defaults to LOD, classic to the flat timeline. */
const VERSION_LAYOUT_KEY = (classic: boolean) => (classic ? 'pf-version-layout-classic' : 'pf-version-layout');

function loadVersionLayout(classic: boolean): VersionLayout {
  const fallback: VersionLayout = classic ? 'timeline' : 'lod';
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(VERSION_LAYOUT_KEY(classic));
    return raw === 'lod' || raw === 'timeline' ? raw : fallback;
  } catch {
    return fallback;
  }
}

function loadClassicView(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(CLASSIC_VIEW_KEY) === '1';
  } catch {
    return false;
  }
}

function loadFilesView(projectId: string): ProjectFilesView {
  if (typeof window === 'undefined') return 'deliverables';
  try {
    const raw = window.localStorage.getItem(FILES_VIEW_KEY(projectId));
    return raw === 'submittals' || raw === 'rfqs' || raw === 'folders' ? raw : 'deliverables';
  } catch {
    return 'deliverables';
  }
}

interface ProjectFilesProps {
  projectId: string;
  leadId?: string;
  leadName?: string;
  dealId?: string;
  /**
   * Deal context (project number / descriptor / clientLabel) used to build the
   * "Release to client portal" filename preview client-side. Filename is also
   * computed authoritatively in the Cloud Function; this is just for UX feedback.
   */
  deal?: Pick<EnhancedDeal, 'projectNumber' | 'clientLabel' | 'descriptor' | 'title'> | null;
  /** From email/notification: `?tab=files&file=&comment=` */
  filesDeepLink?: ProjectFilesDeepLink | null;
  onFilesDeepLinkConsumed?: () => void;
  /** Deep link: `?tab=files&track=<id>` (also supports legacy `stream=`) */
  filesInitialTrackId?: string | null;
  /** Clears `track` / `stream` from the URL after applying (keeps `tab=files`). */
  onTrackDeepLinkConsumed?: () => void;
  /** Deep link: `?tab=files&view=folders&folder=<path>` (from `#files-…` comment chips). */
  filesInitialView?: ProjectFilesView | null;
  filesInitialFolderPath?: string | null;
  /** Clears `view` / `folder` from the URL after applying. */
  onViewDeepLinkConsumed?: () => void;
  /** Deep link: `?tab=files&view=rfqs&request=<releaseId,…>` — Suppliers tab hand-off into the request wizard. */
  filesInitialRequestReleaseIds?: string[] | null;
  /** Clears `request` from the URL once the wizard has been queued. */
  onRequestDeepLinkConsumed?: () => void;
  /** Shown when a files deep link cannot be resolved after data has loaded. */
  onDeepLinkError?: (title: string, body?: string) => void;
  /** Storage path belongs to a different timber project — reopen the same file comment there. */
  onFilesDeepLinkForeignProject?: (projectId: string) => void;
  /** When set, enables Release to vendor for CNC work orders (general/CNC file tracks only). */
  vendorRfqProjectId?: string;
  vendorReleaseOrgId?: string;
  /** CNC quote work order for legacy direct vendor release (general/CNC tracks only). */
  cncRfqProjectId?: string;
  timberProjectName?: string;
  /** Supplier hub / view-as: 3D version tracks only, no upload or release. */
  supplierReadOnly?: boolean;
  /** Architect hub / preview: 2D, HTML, 3D, and general files. */
  architectReadOnly?: boolean;
  /** Rendered at the right end of the toolbar (help / walkthrough button). */
  toolbarExtra?: React.ReactNode;
  /** Navigate to Settings → Project Defaults → Designer Standards. */
  onOpenDesignerStandardsSettings?: () => void;
  /** Open the Suppliers tab for this project (RFQs view "Open in Suppliers"). */
  onOpenSuppliers?: () => void;
}

function FileGroupPopover({
  groups,
  value,
  saving,
  onChange,
  onSave,
  onCancel,
}: {
  groups: { id: string; label: string; detail?: string }[];
  value: string[];
  saving: boolean;
  onChange: (groupIds: string[]) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const allIds = groups.map(group => group.id);
  const allSelected = allIds.length > 0 && allIds.every(id => value.includes(id));
  const toggle = (groupId: string) => {
    onChange(value.includes(groupId) ? value.filter(id => id !== groupId) : [...value, groupId]);
  };
  return (
    <form
      className="pf-track-structure-pop"
      onSubmit={event => {
        event.preventDefault();
        onSave();
      }}
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
    >
      <div className="pf-track-structure-field">
        <span className="pf-vr-mu">Structure groups</span>
        <div className="pf-group-checks" role="group" aria-label="Structure groups for this version">
          <label className="pf-group-check">
            <input
              type="checkbox"
              checked={allSelected}
              disabled={saving}
              onChange={() => onChange(allSelected ? [] : allIds)}
            />
            <span>Whole project</span>
          </label>
          {groups.map(group => (
            <label key={group.id} className="pf-group-check" title={group.detail}>
              <input
                type="checkbox"
                checked={value.includes(group.id)}
                disabled={saving}
                onChange={() => toggle(group.id)}
              />
              <span>{group.label}</span>
            </label>
          ))}
        </div>
        <p className="pf-track-structure-hint">
          {value.length === 0
            ? 'Choose at least one structure group.'
            : allSelected
              ? 'Whole project keeps one shared file. Comments, markup, and edits show on every structure group’s track.'
              : 'Adds this version to each selected group’s track.'}
        </p>
      </div>
      <div className="pf-track-structure-pop-actions">
        <button type="submit" className="pf-version-act" disabled={saving || value.length === 0}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="pf-version-act pf-version-act--muted" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

const ProjectFiles: React.FC<ProjectFilesProps> = ({
  projectId,
  leadId,
  leadName,
  dealId,
  deal,
  filesDeepLink,
  onFilesDeepLinkConsumed,
  filesInitialTrackId,
  onTrackDeepLinkConsumed,
  filesInitialView,
  filesInitialFolderPath,
  onViewDeepLinkConsumed,
  filesInitialRequestReleaseIds,
  onRequestDeepLinkConsumed,
  onDeepLinkError,
  onFilesDeepLinkForeignProject,
  vendorRfqProjectId,
  vendorReleaseOrgId,
  cncRfqProjectId,
  timberProjectName,
  supplierReadOnly = false,
  architectReadOnly = false,
  toolbarExtra,
  onOpenDesignerStandardsSettings,
  onOpenSuppliers,
}) => {
  const partnerReadOnly = supplierReadOnly || architectReadOnly;
  const { user } = useAuth();
  const {
    taskComments: crmTaskComments,
    taskGroups: crmTaskGroups,
    timberProjects,
    projectTasks: crmProjectTasks,
    projectOnboardings: crmProjectOnboardings,
    enhancedDeals,
    supplierOrgs,
    rfqProjects,
    publishRfqPackage,
    updateRfqProject,
  } = useCRM();
  // Rendered inside the Project Hub's selected project — prefer its scoped data
  // over the org-wide CRM caches (which no longer stream by default).
  const scopedHubData = useProjectHubScopedData();
  const taskComments = scopedHubData?.taskComments ?? crmTaskComments;
  const taskGroups = scopedHubData?.taskGroups ?? crmTaskGroups;
  const projectTasks = scopedHubData?.tasks ?? crmProjectTasks;
  const projectOnboardings = scopedHubData?.projectOnboardings ?? crmProjectOnboardings;
  const backfilledPlanReviewCommentsRef = useRef(new Set<string>());
  const { openProjectUploadModal: openUploadModal, uploading, uploadProgress } = useProjectUploadModal();
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [streams, setStreams] = useState<ProjectFileStream[]>([]);
  const [streamsReady, setStreamsReady] = useState(false);
  const [selectedStreamId, setSelectedStreamId] = useState<string | null>(null);
  const [subscribedStreamVersions, setSubscribedStreamVersions] = useState<ProjectFile[]>([]);
  const streamVersions = useMemo(
    () => withSharedStreamVersions(selectedStreamId, subscribedStreamVersions, files),
    [selectedStreamId, subscribedStreamVersions, files],
  );
  const [streamVersionsReady, setStreamVersionsReady] = useState(false);
  const [expandedStages, setExpandedStages] = useState<ReadonlySet<ProjectFileDetailStage>>(() => new Set());
  const stageExpandKeyRef = useRef('');
  const [streamPlanReviews, setStreamPlanReviews] = useState<PlanReview[]>([]);
  const [planReviewStartingId, setPlanReviewStartingId] = useState<string | null>(null);
  const [deletingStreamId, setDeletingStreamId] = useState<string | null>(null);
  const trackDeepLinkHandledRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [folderFilesError, setFolderFilesError] = useState<string | null>(null);
  const [financialFiles, setFinancialFiles] = useState<ProjectFile[]>([]);
  const [filesRetryNonce, setFilesRetryNonce] = useState(0);
  const [projectAnnotations, setProjectAnnotations] = useState<PdfAnnotation[]>([]);
  const deepLinkHandledKeyRef = useRef<string | null>(null);
  const deepLinkFailedKeyRef = useRef<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [unreleasingId, setUnreleasingId] = useState<string | null>(null);
  const [imageViewerDoc, setImageViewerDoc] = useState<{
    url: string;
    title: string;
    documentId: string;
    mimeType?: string;
    annotationId?: string;
    sharedAcrossStructures?: boolean;
  } | null>(null);
  const [pdfViewerDoc, setPdfViewerDoc] = useState<{
    url: string;
    title: string;
    documentId: string;
    annotationId?: string;
    pageNumber?: number;
    leadId?: string;
    streamId?: string;
    siblingVersions?: PdfViewerSiblingVersion[];
    initialCompareDocumentId?: string;
    sharedAcrossStructures?: boolean;
  } | null>(null);
  const [csvViewerDoc, setCsvViewerDoc] = useState<{
    url: string;
    title: string;
    documentId: string;
    annotationId?: string;
    sharedAcrossStructures?: boolean;
    trueDimensions?: boolean;
  } | null>(null);
  const [htmlViewerDoc, setHtmlViewerDoc] = useState<{
    url: string;
    fetchUrl: string;
    title: string;
    versionLabel?: string;
    documentId?: string;
    projectId?: string;
    projectName?: string;
    streamId?: string;
    siblingModels?: HtmlViewerSibling[];
    sharedAcrossStructures?: boolean;
  } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  const [streamRenameId, setStreamRenameId] = useState<string | null>(null);
  const [streamRenameValue, setStreamRenameValue] = useState('');
  const [streamRenameAnchor, setStreamRenameAnchor] = useState<'rail' | 'header'>('header');
  const [versionEdit, setVersionEdit] = useState<{
    fileId: string;
    label: string;
    detailStage: ProjectFileDetailStage | '';
    trueDimensions: boolean;
    forCnc: boolean;
    structureIds: string[];
  } | null>(null);
  /** Checkbox wins over the saved flag while this version's edit form is open. */
  const viewerTrueDimensions = useCallback(
    (file: Pick<ProjectFile, 'id' | 'trueDimensions'>) => {
      if (versionEdit && file.id && versionEdit.fileId === file.id) return versionEdit.trueDimensions;
      return file.trueDimensions === true;
    },
    [versionEdit],
  );
  useEffect(() => {
    setCsvViewerDoc((current) => {
      if (!current) return current;
      const file = files.find((f) => f.id === current.documentId || f.url === current.documentId);
      const editingThisFile =
        versionEdit != null &&
        (versionEdit.fileId === current.documentId || (file?.id != null && versionEdit.fileId === file.id));
      const next = editingThisFile && versionEdit ? versionEdit.trueDimensions : file?.trueDimensions === true;
      if (Boolean(current.trueDimensions) === Boolean(next)) return current;
      return { ...current, trueDimensions: next };
    });
  }, [versionEdit, files]);
  const [proposalStructures, setProposalStructures] = useState<StructureRef[]>([]);
  const [renameSaving, setRenameSaving] = useState(false);

  // ── Files redesign: views, classic toggle, project-wide release feeds ──
  const [classicViewPref, setClassicView] = useState<boolean>(() => loadClassicView());
  // TEMPORARY pilot gate — see REMOVAL CHECKLIST in utils/filesRedesignAccess.ts.
  // Users outside the pilot are held on the classic layout (also while the flag loads).
  const redesign = useFilesRedesignAccess();
  // Every architect, including staff preview, opens the redesigned Files page
  // (Deliverables and Other files). The pilot flag and a saved Classic checkbox
  // do not put them back on the single-page layout.
  const classicView = architectReadOnly ? false : (!redesign.enabled || classicViewPref);
  const [filesView, setFilesViewState] = useState<ProjectFilesView>(() => loadFilesView(projectId));
  const setFilesView = useCallback(
    (next: ProjectFilesView) => {
      setFilesViewState(next);
      try {
        window.localStorage.setItem(FILES_VIEW_KEY(projectId), next);
      } catch {
        /* ignore */
      }
    },
    [projectId],
  );
  useEffect(() => {
    setFilesViewState(loadFilesView(projectId));
  }, [projectId]);
  const toggleClassicView = useCallback(() => {
    setClassicView(prev => {
      const next = !prev;
      try {
        window.localStorage.setItem(CLASSIC_VIEW_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);
  const [versionLayout, setVersionLayoutState] = useState<VersionLayout>(() => loadVersionLayout(classicView));
  useEffect(() => {
    setVersionLayoutState(loadVersionLayout(classicView));
  }, [classicView]);
  const setVersionLayout = useCallback(
    (next: VersionLayout) => {
      setVersionLayoutState(next);
      try {
        window.localStorage.setItem(VERSION_LAYOUT_KEY(classicView), next);
      } catch {
        /* ignore */
      }
    },
    [classicView],
  );
  // Walkthrough: switch views / select a folder so the highlighted element is mounted,
  // then restore whatever the user was looking at when the tour ends.
  const [tourFolderPath, setTourFolderPath] = useState<string | null>(null);
  const tourRestoreViewRef = useRef<ProjectFilesView | null>(null);
  useEffect(() => {
    const onTour = (evt: Event) => {
      const detail = (evt as CustomEvent<ProjectFilesTourEventDetail>).detail;
      if (!detail) return;
      if (detail.type === 'start') {
        tourRestoreViewRef.current = filesView;
        return;
      }
      if (detail.type === 'setView') {
        if (!classicView) setFilesViewState(detail.view);
        setTourFolderPath(detail.folderPath ?? null);
        return;
      }
      if (detail.type === 'end') {
        setTourFolderPath(null);
        if (tourRestoreViewRef.current) setFilesViewState(tourRestoreViewRef.current);
        tourRestoreViewRef.current = null;
      }
    };
    window.addEventListener(PROJECT_FILES_TOUR_EVENT, onTour);
    return () => window.removeEventListener(PROJECT_FILES_TOUR_EVENT, onTour);
  }, [filesView, classicView]);
  const [dealPortalReleases, setDealPortalReleases] = useState<PortalFileRelease[]>([]);
  const [projectHubReleases, setProjectHubReleases] = useState<RfqHubFileRelease[]>([]);
  const [projectVendorReleases, setProjectVendorReleases] = useState<VendorFileRelease[]>([]);
  const [rfqPackages, setRfqPackages] = useState<RfqPackage[]>([]);
  const [rfqSubmissions, setRfqSubmissions] = useState<RfqSubmission[]>([]);
  const [rfqProjectApprovals, setRfqProjectApprovals] = useState<Map<string, RfqProjectApprovalFields>>(
    () => new Map(),
  );
  const [rfqFeedsReady, setRfqFeedsReady] = useState(false);
  const [rfqFeedError, setRfqFeedError] = useState<string | null>(null);
  const [rfqFeedNonce, setRfqFeedNonce] = useState(0);
  const [supplierNames, setSupplierNames] = useState<Map<string, string>>(() => new Map());
  // RFQ request rounds (pilot) — Files → RFQs becomes the request builder + history.
  const rfqRequestsGate = useRfqRequestsAccess();
  const [rfqRequests, setRfqRequests] = useState<RfqRequest[]>([]);
  const [requestWizard, setRequestWizard] = useState<{
    initialStreamId: string | null;
    initialStreamIds?: string[] | null;
    initialReleaseIds?: string[] | null;
  } | null>(null);
  /** Staged release ids from a `?request=` deep link, held until the RFQ feeds and gate are ready. */
  const [pendingRequestReleaseIds, setPendingRequestReleaseIds] = useState<string[] | null>(null);
  const [requestBusy, setRequestBusy] = useState(false);
  const [designerStandards, setDesignerStandards] = useState<DesignerStandardItem[]>([]);
  const [designerStandardsError, setDesignerStandardsError] = useState<string | null>(null);
  const [folderUploadTarget, setFolderUploadTarget] = useState<string | null>(null);

  // Portal release state (Project Hub -> Client Portal).
  const canMutateFiles = !partnerReadOnly;
  const canEditVersionLabel = canEditProjectUploadVersionLabel({
    authRole: user?.role,
    designations: user?.designations,
  });
  /** Annotations require a leadId; architects (and unlinked projects) may only have the timber project id. */
  const viewerLeadId = useMemo(
    () =>
      resolveFileViewerLeadId(
        leadId,
        dealId ? enhancedDeals.find(d => d.id === dealId)?.leadId : undefined,
        projectId,
      ),
    [leadId, dealId, enhancedDeals, projectId],
  );
  const canRelease = canMutateFiles && canAccessProjectHubClientPortalControls(user?.role, user?.designations);
  const [portalId, setPortalId] = useState<string | null>(null);
  const [dealApprovals, setDealApprovals] = useState<PortalApprovalTargetGroup>(emptyPortalApprovalTargetGroup);
  const [streamReleases, setStreamReleases] = useState<PortalFileRelease[]>([]);
  const [releaseModalState, setReleaseModalState] = useState<
    | {
        streamId: string;
        streamKind: ReleasableStreamKind;
        streamTitle: string;
        initialVersionId?: string | null;
      }
    | null
  >(null);
  const [releaseToast, setReleaseToast] = useState<string | null>(null);
  const [vendorReleaseModalState, setVendorReleaseModalState] = useState<
    | { streamId: string; streamTitle: string; initialVersionId?: string | null }
    | null
  >(null);
  const [stagingReleaseModalState, setStagingReleaseModalState] = useState<
    | {
        streamId: string;
        streamKind: RfqStagingReleasableStreamKind;
        streamTitle: string;
        initialVersionId?: string | null;
      }
    | null
  >(null);
  const [streamHubReleases, setStreamHubReleases] = useState<RfqHubFileRelease[]>([]);
  const canReleaseToVendor = canRelease && !!vendorRfqProjectId && !!vendorReleaseOrgId && !!dealId;
  const canReleaseToSuppliers = canRelease && !!dealId;
  const streamRenameHeaderRef = useRef<HTMLInputElement>(null);
  const streamRenameSidebarRef = useRef<HTMLInputElement>(null);
  const versionEditLabelRef = useRef<HTMLInputElement>(null);
  const skipStreamRenameBlurRef = useRef(false);

  useEffect(() => {
    if (supplierReadOnly) {
      setFiles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFilesError(null);
    const subscribe = architectReadOnly
      ? subscribeArchitectUnversionedProjectFiles
      : subscribeProjectFiles;
    const unsub = subscribe(
      projectId,
      (docs) => {
        setFiles(docs);
        setFilesError(null);
        setFolderFilesError(null);
        setLoading(false);
      },
      (err) => {
        if (architectReadOnly) {
          setFolderFilesError(err?.message || "Couldn't load other project files.");
          setLoading(false);
          return;
        }
        setFilesError(err?.message || "Couldn't load project files.");
        setLoading(false);
      },
    );
    return () => unsub();
  }, [projectId, supplierReadOnly, architectReadOnly, filesRetryNonce]);

  const includeFinancials = canSeeProjectFinancials(user);
  const folderTree = useMemo(
    () => projectFoldersForViewer(includeFinancials),
    [includeFinancials],
  );

  useEffect(() => {
    if (!includeFinancials || supplierReadOnly) {
      setFinancialFiles([]);
      return;
    }
    return subscribeProjectFinancialFiles(
      projectId,
      setFinancialFiles,
      () => setFinancialFiles([]),
    );
  }, [projectId, includeFinancials, supplierReadOnly]);

  useEffect(() => {
    const streamKinds = architectReadOnly
      ? ARCHITECT_HUB_FILE_STREAM_KINDS
      : supplierReadOnly
        ? SUPPLIER_HUB_FILE_STREAM_KINDS
        : undefined;
    const unsub = subscribeProjectFileStreams(
      projectId,
      list => {
        setStreams(list);
        setStreamsReady(true);
      },
      streamKinds ? { streamKinds } : undefined,
    );
    return () => unsub();
  }, [projectId, supplierReadOnly, architectReadOnly]);

  // Structure forks inherit the pre-split history of the root they were forked from.
  // A structure added later only inherits root files uploaded after it was added.
  const selectedStreamRecord = useMemo(
    () => streams.find(s => s.id === selectedStreamId) ?? null,
    [streams, selectedStreamId],
  );
  const selectedRootStreamId = selectedStreamRecord?.forkedFromStreamId ?? null;
  const selectedInheritAfter = useMemo(
    () => (selectedStreamRecord ? effectiveInheritVersionsAfter(selectedStreamRecord, streams) ?? null : null),
    [selectedStreamRecord, streams],
  );
  useEffect(() => {
    if (!selectedStreamId) {
      setSubscribedStreamVersions([]);
      setStreamVersionsReady(false);
      return;
    }
    setStreamVersionsReady(false);
    const unsub = subscribeProjectFileVersionsForStream(
      projectId,
      selectedStreamId,
      versions => {
        setSubscribedStreamVersions(versions);
        setStreamVersionsReady(true);
      },
      selectedRootStreamId,
      selectedInheritAfter,
    );
    return () => unsub();
  }, [projectId, selectedStreamId, selectedRootStreamId, selectedInheritAfter]);

  const versionStageTree = useMemo(
    () => groupProjectFileVersionsByStage(streamVersions),
    [streamVersions],
  );
  const newestVersionKey = useMemo(() => {
    const newest = streamVersions[0];
    return newest ? `${newest.id ?? ''}:${newest.uploadedAt?.getTime() ?? ''}:${newest.detailStage ?? ''}` : '';
  }, [streamVersions]);

  useEffect(() => {
    const key = `${selectedStreamId ?? ''}|${newestVersionKey}`;
    if (stageExpandKeyRef.current === key) return;
    stageExpandKeyRef.current = key;
    const open = versionStageTree.stages.find(group => group.containsNewest)?.stage;
    setExpandedStages(open ? new Set([open]) : new Set());
  }, [selectedStreamId, newestVersionKey, versionStageTree.stages]);

  const toggleVersionStage = (stage: ProjectFileDetailStage) => {
    setExpandedStages(prev => {
      const next = new Set(prev);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  };

  const visibleStreams = useMemo(() => {
    if (supplierReadOnly) return streams.filter(s => isSupplierHubVisibleStreamKind(s.streamKind));
    if (architectReadOnly) return streams.filter(s => isArchitectHubVisibleStreamKind(s.streamKind));
    return streams;
  }, [streams, supplierReadOnly, architectReadOnly]);

  // Once a whole-project track has been split, its versions open every structure
  // track forked from it — so the read-only root is not listed (on either view).
  const railStreams = useMemo(
    () => visibleStreams.filter(s => !s.wholeProjectRoot).slice().sort(compareStreamsByRailOrder),
    [visibleStreams],
  );
  /** Id-keyed options with current names (proposal preferred; stream tags for designers). */
  const hubStructures = useMemo(
    () => buildHubStructureOptions(proposalStructures, streams),
    [proposalStructures, streams],
  );
  const railGroups = useMemo(() => groupStreamsByStructure(railStreams, hubStructures), [railStreams, hubStructures]);
  /** Old links (notifications, comment chips) still point at split roots — land on the forked track instead. */
  const redirectHiddenRoot = useCallback(
    (streamId: string | null): string | null => {
      if (!streamId) return streamId;
      const s = visibleStreams.find(x => x.id === streamId);
      if (!s?.wholeProjectRoot) return streamId;
      const forked = (s.forkedToStreamIds ?? []).filter(id => railStreams.some(r => r.id === id));
      if (forked.length === 0) return streamId;
      const firstGroupKey = railGroups.find(g => g.structureId !== WHOLE_PROJECT_GROUP_ID)?.structureId;
      const inFirstGroup = forked.find(id => {
        const fs = railStreams.find(r => r.id === id);
        return fs && groupKeyForStream(fs) === firstGroupKey;
      });
      return inFirstGroup ?? forked[0];
    },
    [visibleStreams, railStreams, railGroups],
  );

  /**
   * A file link into a hidden root: the forked tracks inherit the root's versions, so the
   * same file simply opens from the forked track. (Legacy split copies, if any remain,
   * are preferred so the row that opens is the one listed in that track.)
   */
  const resolveHiddenRootFile = useCallback(
    (match: ProjectFile): ProjectFile => {
      if (!match.streamId) return match;
      const s = visibleStreams.find(x => x.id === match.streamId);
      if (!s?.wholeProjectRoot) return match;
      const targetStream = redirectHiddenRoot(match.streamId);
      if (!targetStream || targetStream === match.streamId) return match;
      const legacyCopy = files.find(f => f.streamId === targetStream && f.wholeProjectOrigin?.fileId === match.id);
      return legacyCopy ?? { ...match, streamId: targetStream };
    },
    [visibleStreams, files, redirectHiddenRoot],
  );

  useEffect(() => {
    if (!filesInitialTrackId || !streamsReady) return;
    if (trackDeepLinkHandledRef.current === filesInitialTrackId) return;
    trackDeepLinkHandledRef.current = filesInitialTrackId;
    const exists = visibleStreams.some(s => s.id === filesInitialTrackId);
    if (exists) setSelectedStreamId(redirectHiddenRoot(filesInitialTrackId));
    onTrackDeepLinkConsumed?.();
  }, [filesInitialTrackId, streamsReady, visibleStreams, onTrackDeepLinkConsumed, redirectHiddenRoot]);

  useEffect(() => {
    if (filesInitialTrackId) return;
    if (railStreams.length === 0) return;
    if (selectedStreamId && railStreams.some(s => s.id === selectedStreamId)) return;
    // Selected a hidden root (Submittals jump, post-split, stale state)? Follow it to its forked track.
    const redirected = redirectHiddenRoot(selectedStreamId);
    if (redirected && redirected !== selectedStreamId && railStreams.some(s => s.id === redirected)) {
      setSelectedStreamId(redirected);
      return;
    }
    setSelectedStreamId(railStreams[0].id || null);
  }, [railStreams, selectedStreamId, filesInitialTrackId, redirectHiddenRoot]);

  /**
   * Rail sections: one per structure group, on both views. Live untagged tracks
   * (uploaded whole-project after a split) trail the list; there is never a
   * "Whole project" heading on an unsplit, untagged project — that stays a flat list.
   */
  const railSections = useMemo(() => {
    if (!anyStreamTagged(railStreams)) return null;
    const tagged = railGroups.filter(g => g.structureId !== WHOLE_PROJECT_GROUP_ID);
    const untagged = railGroups.find(g => g.structureId === WHOLE_PROJECT_GROUP_ID);
    return untagged && untagged.streams.length > 0 ? [...tagged, untagged] : tagged;
  }, [railStreams, railGroups]);
  /** Group label used to shorten a track's title and for the detail-header crumb. */
  const railGroupForStream = useCallback(
    (streamId: string | undefined) => (railSections ? railSections.find(g => g.streams.some(s => s.id === streamId)) : undefined),
    [railSections],
  );

  // Reuse the Hub's project-scoped annotations subscription when available;
  // only self-subscribe when mounted without the scoped provider.
  const scopedAnnotations = scopedHubData?.projectId === projectId ? scopedHubData.pdfAnnotations : null;
  const hasScopedAnnotations = scopedAnnotations !== null;
  useEffect(() => {
    if (hasScopedAnnotations) return;
    return subscribeToAnnotationsForProject(projectId, setProjectAnnotations, {
      decisionOnly: architectReadOnly,
    });
  }, [architectReadOnly, projectId, hasScopedAnnotations]);
  useEffect(() => {
    if (scopedAnnotations) setProjectAnnotations(scopedAnnotations);
  }, [scopedAnnotations]);

  /**
   * Resolve the client portal that owns this deal so the release modal has a
   * target. Run once per dealId — the lookup hits 1-2 indexed Firestore reads
   * and the result is reused for every release in this Project Hub session.
   * Designers/engineers are denied clientPortals by rules — skip lookup (lose
   * "client approved" badges; accepted).
   */
  const canLoadClientPortal =
    canAccessProjectHubClientPortalControls(user?.role, user?.designations);
  useEffect(() => {
    if (!dealId || partnerReadOnly || !canLoadClientPortal) {
      setPortalId(null);
      return undefined;
    }
    let cancelled = false;
    void findPortalIdForDeal(dealId)
      .then(id => {
        if (!cancelled) setPortalId(id);
      })
      .catch(() => {
        if (!cancelled) setPortalId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dealId, partnerReadOnly, canLoadClientPortal]);

  useEffect(() => {
    if (!portalId || !dealId || !canLoadClientPortal) {
      setDealApprovals(emptyPortalApprovalTargetGroup());
      return undefined;
    }
    return onSnapshot(doc(db, 'clientPortals', portalId), snap => {
      const data = (snap.data() || {}) as Record<string, unknown>;
      const rawProjects = data.projects;
      const rawProject =
        rawProjects && typeof rawProjects === 'object'
          ? (rawProjects as Record<string, unknown>)[dealId]
          : undefined;
      const project =
        rawProject && typeof rawProject === 'object'
          ? parsePortalProjectRow(dealId, rawProject as Record<string, unknown>)
          : undefined;
      const root = parsePortalProjectRow(dealId, data);
      setDealApprovals(
        groupPortalDealApprovals({
          plansApproval: project?.plansApproval ?? root.plansApproval,
          plansApprovalHistory: project?.plansApprovalHistory ?? root.plansApprovalHistory,
          modelApproval: project?.modelApproval ?? root.modelApproval,
          modelApprovalHistory: project?.modelApprovalHistory ?? root.modelApprovalHistory,
          conceptApproval: project?.conceptApproval ?? root.conceptApproval,
          conceptApprovalHistory: project?.conceptApprovalHistory ?? root.conceptApprovalHistory,
        }),
      );
    });
  }, [portalId, dealId, canLoadClientPortal]);

  /**
   * Subscribe to releases for the currently selected stream so we can render the
   * "Released" pill on each version in the timeline. Uses a collection-group
   * query so we don't need the parent `portalId` here.
   */
  // Structure forks also show releases made from the root they inherit from.
  // Designers/engineers/architects are denied the collection-group rule — skip.
  useEffect(() => {
    if (!selectedStreamId) {
      setStreamReleases([]);
      return;
    }
    if (!canReadPortalFileReleaseCollectionGroup(user?.role)) {
      setStreamReleases([]);
      return undefined;
    }
    if (!selectedRootStreamId) return subscribeStreamReleases(projectId, selectedStreamId, setStreamReleases);
    let own: PortalFileRelease[] = [];
    let root: PortalFileRelease[] = [];
    const emit = () => setStreamReleases([...own, ...root]);
    const u1 = subscribeStreamReleases(projectId, selectedStreamId, r => { own = r; emit(); });
    const u2 = subscribeStreamReleases(projectId, selectedRootStreamId, r => { root = r; emit(); });
    return () => { u1(); u2(); };
  }, [projectId, selectedStreamId, selectedRootStreamId, user?.role]);

  useEffect(() => {
    if (!selectedStreamId) {
      setStreamHubReleases([]);
      return;
    }
    if (!selectedRootStreamId) return subscribeProjectStreamHubReleases(projectId, selectedStreamId, setStreamHubReleases);
    let own: RfqHubFileRelease[] = [];
    let root: RfqHubFileRelease[] = [];
    const emit = () => setStreamHubReleases([...own, ...root]);
    const u1 = subscribeProjectStreamHubReleases(projectId, selectedStreamId, r => { own = r; emit(); });
    const u2 = subscribeProjectStreamHubReleases(projectId, selectedRootStreamId, r => { root = r; emit(); });
    return () => { u1(); u2(); };
  }, [projectId, selectedStreamId, selectedRootStreamId]);

  const releasesByFileId = useMemo(() => {
    const map = new Map<string, PortalFileRelease[]>();
    for (const r of streamReleases) {
      const arr = map.get(r.sourceFileId) || [];
      arr.push(r);
      map.set(r.sourceFileId, arr);
    }
    map.forEach(arr =>
      arr.sort(
        (a: PortalFileRelease, b: PortalFileRelease) =>
          b.releaseVersionIndex - a.releaseVersionIndex,
      ),
    );
    return map;
  }, [streamReleases]);

  /** Source files the client currently sees (newest release per portal slot). */
  const portalHeadFileIds = useMemo(() => {
    const newest = new Map<string, PortalFileRelease>();
    for (const r of dealPortalReleases) {
      const key = r.portalTarget || r.streamKind || 'default';
      const cur = newest.get(key);
      if (!cur || r.releasedAt.getTime() > cur.releasedAt.getTime()) newest.set(key, r);
    }
    return new Set(Array.from(newest.values()).map(r => r.sourceFileId));
  }, [dealPortalReleases]);

  /** Deal-wide releases keyed by source file — lets forked "Whole project" versions show the root's release/approval. */
  const dealReleasesByFileId = useMemo(() => {
    const map = new Map<string, PortalFileRelease[]>();
    for (const r of dealPortalReleases) {
      const arr = map.get(r.sourceFileId) || [];
      arr.push(r);
      map.set(r.sourceFileId, arr);
    }
    map.forEach(arr => arr.sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex));
    return map;
  }, [dealPortalReleases]);

  const hubReleasesByFileId = useMemo(() => {
    const map = new Map<string, RfqHubFileRelease[]>();
    for (const r of streamHubReleases) {
      const arr = map.get(r.sourceFileId) || [];
      arr.push(r);
      map.set(r.sourceFileId, arr);
    }
    map.forEach((arr) => {
      arr.sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex);
    });
    return map;
  }, [streamHubReleases]);

  // Auto-dismiss the release toast after a few seconds (keeps the UI quiet per ui-ux-pro-max).
  useEffect(() => {
    if (!releaseToast) return;
    const t = window.setTimeout(() => setReleaseToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [releaseToast]);

  const pdfUnreadByFileKey = useMemo(() => {
    const set = new Set<string>();
    if (!user?.id) return set;
    for (const ann of projectAnnotations) {
      if (!pdfAnnotationHasUnreadForUser(ann, user.id)) continue;
      set.add(ann.documentId);
      if (ann.documentUrl) set.add(ann.documentUrl);
    }
    return set;
  }, [projectAnnotations, user?.id]);

  const fileHasUnreadFileComment = useCallback(
    (file: ProjectFile) => {
      const id = file.id || '';
      return (id && pdfUnreadByFileKey.has(id)) || pdfUnreadByFileKey.has(file.url);
    },
    [pdfUnreadByFileKey]
  );

  const getFileCommentCount = useCallback(
    (file: ProjectFile) =>
      user?.id ? countUnreadCommentsOnProjectFile(projectAnnotations, file, user.id) : 0,
    [projectAnnotations, user?.id],
  );

  const fileIsSharedAcrossStructures = useCallback(
    (file: ProjectFile) => streams.some(stream => stream.id === file.streamId && stream.wholeProjectRoot === true),
    [streams],
  );

  const openFileViewerForDeepLink = useCallback(
    (
      match: ProjectFile,
      annotationId?: string,
      pageNumber?: number,
      annotationLeadId?: string,
    ) => {
      const sharedAcrossStructures = fileIsSharedAcrossStructures(match);
      if (isPdfType(match.mimeType)) {
        setPdfViewerDoc({
          url: portalHostedAssetHref(match.url, { downloadName: match.name }),
          title: match.name,
          documentId: match.id || match.url,
          annotationId,
          pageNumber,
          leadId: annotationLeadId,
          streamId: match.streamId,
          sharedAcrossStructures,
        });
      } else if (isImageType(match.mimeType)) {
        setImageViewerDoc({
          url: portalHostedAssetHref(match.url, { downloadName: match.name }),
          title: match.name,
          documentId: match.id || match.url,
          mimeType: match.mimeType,
          annotationId,
          sharedAcrossStructures,
        });
      } else if (isCsvType(match.mimeType, match.name)) {
        setCsvViewerDoc({
          url: portalHostedAssetHref(match.url, { downloadName: match.name }),
          title: match.name,
          documentId: match.id || match.url,
          annotationId,
          sharedAcrossStructures,
          trueDimensions: viewerTrueDimensions(match),
        });
      } else {
        return false;
      }
      return true;
    },
    [fileIsSharedAcrossStructures, viewerTrueDimensions],
  );

  useEffect(() => {
    if (!filesDeepLink?.documentId || loading || !streamsReady) return;

    let cancelled = false;
    void (async () => {
      const { documentId, annotationId } = filesDeepLink;
      let pageNumber = filesDeepLink.pageNumber;
      let annotationLeadId: string | undefined;

      if (annotationId) {
        try {
          const snap = await getDoc(doc(db, 'pdfAnnotations', annotationId));
          if (snap.exists()) {
            const data = snap.data();
            const raw = data.pageNumber;
            if (pageNumber == null && typeof raw === 'number' && Number.isFinite(raw)) {
              pageNumber = raw;
            }
            const rawLeadId = data.leadId;
            if (typeof rawLeadId === 'string' && rawLeadId.trim()) {
              annotationLeadId = rawLeadId.trim();
            }
            const annotationHint = {
              documentId: typeof data.documentId === 'string' ? data.documentId : documentId,
              documentUrl: typeof data.documentUrl === 'string' ? data.documentUrl : '',
            };
            if (cancelled) return;

            const key = `${documentId}\0${annotationId ?? ''}\0${pageNumber ?? ''}`;
            if (deepLinkHandledKeyRef.current === key) return;

            // Partner hubs keep `files` empty and only load the selected track's
            // versions — search both, then fall back to a live projectFiles fetch
            // (never open a stale annotation.documentUrl as the PDF bytes).
            let match =
              findProjectFileForDeepLink(files, documentId, annotationHint) ||
              findProjectFileForDeepLink(streamVersions, documentId, annotationHint);

            if (!match) {
              const resolved = await resolveAnnotationViewerOpenTarget({
                documentId: annotationHint.documentId || documentId,
                documentUrl: annotationHint.documentUrl,
              });
              if (cancelled) return;
              if (resolved?.url) {
                const stored =
                  parseProjectFilesStoragePath(resolved.storagePath || resolved.url) ||
                  parseProjectFilesStoragePath(annotationHint.documentUrl);
                if (stored && stored.projectId !== projectId && onFilesDeepLinkForeignProject) {
                  onFilesDeepLinkForeignProject(stored.projectId);
                  return;
                }
                const lower = (resolved.title || '').toLowerCase();
                let mimeType = resolved.mimeType || 'application/pdf';
                if (!resolved.mimeType) {
                  if (/\.(png|jpe?g|gif|webp)$/i.test(lower)) mimeType = 'image/png';
                  else if (/\.csv$/i.test(lower)) mimeType = 'text/csv';
                }
                match = {
                  id: resolved.documentId || documentId,
                  projectId: resolved.projectId || stored?.projectId || projectId,
                  name: resolved.title || displayNameFromFileRef(resolved.url),
                  url: resolved.url,
                  storagePath:
                    resolved.storagePath ||
                    (stored ? `projectFiles/${stored.projectId}/${stored.objectName}` : ''),
                  mimeType,
                  size: 0,
                  uploadedAt: new Date(),
                  uploadedBy: '',
                  uploadedByName: '',
                  streamId: resolved.streamId,
                };
              }
            }

            if (!match) {
              if (deepLinkFailedKeyRef.current === key) return;
              deepLinkFailedKeyRef.current = key;
              onDeepLinkError?.(
                'Could not open this file comment',
                'The file may have been removed or you may not have access. Try opening it from the Files tab.',
              );
              return;
            }
            match = resolveHiddenRootFile(match);

            const streamToSelect = match.streamId || filesInitialTrackId || null;
            if (streamToSelect && streamToSelect !== selectedStreamId) {
              setSelectedStreamId(streamToSelect);
            }

            if (!openFileViewerForDeepLink(match, annotationId, pageNumber, annotationLeadId)) {
              if (deepLinkFailedKeyRef.current === key) return;
              deepLinkFailedKeyRef.current = key;
              onDeepLinkError?.(
                'Could not open this file comment',
                'This file type cannot be opened with inline comments.',
              );
              return;
            }

            deepLinkHandledKeyRef.current = key;
            deepLinkFailedKeyRef.current = null;
            onFilesDeepLinkConsumed?.();
            return;
          }
        } catch {
          /* fall through — PdfViewerModal will jump once annotations load */
        }
      }

      if (cancelled) return;

      const key = `${documentId}\0${annotationId ?? ''}\0${pageNumber ?? ''}`;
      if (deepLinkHandledKeyRef.current === key) return;

      let found =
        findProjectFileForDeepLink(files, documentId) ||
        findProjectFileForDeepLink(streamVersions, documentId);
      if (!found) {
        const resolved = await resolveAnnotationViewerOpenTarget({ documentId });
        if (cancelled) return;
        if (resolved?.url) {
          found = {
            id: resolved.documentId || documentId,
            projectId: resolved.projectId || projectId,
            name: resolved.title || displayNameFromFileRef(resolved.url),
            url: resolved.url,
            storagePath: resolved.storagePath || '',
            mimeType: resolved.mimeType || 'application/pdf',
            size: 0,
            uploadedAt: new Date(),
            uploadedBy: '',
            uploadedByName: '',
            streamId: resolved.streamId,
          };
        }
      }
      if (!found) {
        if (deepLinkFailedKeyRef.current === key) return;
        deepLinkFailedKeyRef.current = key;
        onDeepLinkError?.(
          'Could not open this file comment',
          'The file may have been removed or you may not have access. Try opening it from the Files tab.',
        );
        return;
      }
      const match = resolveHiddenRootFile(found);

      const streamToSelect = match.streamId || filesInitialTrackId || null;
      if (streamToSelect && streamToSelect !== selectedStreamId) {
        setSelectedStreamId(streamToSelect);
      }

      if (!openFileViewerForDeepLink(match, annotationId, pageNumber, annotationLeadId)) {
        if (deepLinkFailedKeyRef.current === key) return;
        deepLinkFailedKeyRef.current = key;
        onDeepLinkError?.(
          'Could not open this file comment',
          'This file type cannot be opened with inline comments.',
        );
        return;
      }

      deepLinkHandledKeyRef.current = key;
      deepLinkFailedKeyRef.current = null;
      onFilesDeepLinkConsumed?.();
    })();

    return () => {
      cancelled = true;
    };
  }, [
    filesDeepLink,
    loading,
    streamsReady,
    files,
    streamVersions,
    projectId,
    filesInitialTrackId,
    selectedStreamId,
    onFilesDeepLinkConsumed,
    onDeepLinkError,
    onFilesDeepLinkForeignProject,
    openFileViewerForDeepLink,
    resolveHiddenRootFile,
  ]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (supplierReadOnly) return;
    setDragOver(true);
  }, [supplierReadOnly]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDelete = useCallback(async (file: ProjectFile) => {
    if (supplierReadOnly) return;
    const hubForFile = file.id ? hubReleasesByFileId.get(file.id) : undefined;
    const stagedNote = hubForFile?.length
      ? `\n\nThis will also remove the supplier staged release (v${hubForFile[0].releaseVersionIndex}).`
      : '';
    const sharedNote = (file.sharedOnStreamIds ?? []).length > 0
      ? '\n\nThis is one file shared with another structure. Removing it removes it there too.'
      : '';
    if (!window.confirm(`Delete "${file.name}"?${stagedNote}${sharedNote}`)) return;
    setDeletingId(file.id!);
    try {
      if (file.employeeOnly) {
        await deleteProjectFinancialFile(file.id!, file.storagePath);
      } else {
        await deleteProjectFile(file.id!, file.storagePath, {
          projectId,
          dealId,
          fromStreamId: file.streamId && selectedStreamId && file.streamId !== selectedStreamId ? selectedStreamId : undefined,
        });
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      alert(err instanceof Error && /shared by every structure track/.test(err.message) ? err.message : 'Failed to delete file.');
    } finally {
      setDeletingId(null);
    }
  }, [projectId, dealId, hubReleasesByFileId, supplierReadOnly, selectedStreamId]);

  const handleUnrelease = useCallback(
    async (file: ProjectFile, releases: PortalFileRelease[]) => {
      if (!portalId || !file.id || releases.length === 0) return;
      const versionIndexes = [...releases]
        .map(r => r.releaseVersionIndex)
        .sort((a, b) => b - a);
      const releaseSummary =
        versionIndexes.length > 1
          ? `portal releases v${versionIndexes.join(', v')}`
          : `portal release v${versionIndexes[0]}`;
      if (
        !window.confirm(
          `Un-release "${file.name}" from the client portal?\n\nThis removes ${releaseSummary}. Clients will no longer see this hub version in their deliverable history. If it is the file currently shown on the portal, the previous release will be restored when one exists.`,
        )
      ) {
        return;
      }
      setUnreleasingId(file.id);
      try {
        await unreleaseProjectFileFromPortal({ portalId, sourceFileId: file.id });
        setReleaseToast(`Removed ${file.name} from the client portal.`);
      } catch (err) {
        console.error('Un-release failed:', err);
        alert(err instanceof Error ? err.message : 'Failed to un-release from client portal.');
      } finally {
        setUnreleasingId(null);
      }
    },
    [portalId],
  );

  const handleDeleteEmptyTrack = useCallback(
    async (track: ProjectFileStream) => {
      if (partnerReadOnly) return;
      if (!track.id || !isVersionTrackWithNoFiles(track)) return;
      if (
        !window.confirm(`Delete empty version history “${track.title}”? This cannot be undone.`)
      )
        return;
      setDeletingStreamId(track.id);
      try {
        await deleteProjectFileStream(projectId, track.id);
        if (selectedStreamId === track.id) setSelectedStreamId(null);
      } catch (err) {
        console.error('Delete version history:', err);
        alert(err instanceof Error ? err.message : 'Could not delete version history.');
      } finally {
        setDeletingStreamId(null);
      }
    },
    [projectId, selectedStreamId, supplierReadOnly],
  );

  const htmlPreviewHref = useCallback(
    (file: Pick<ProjectFile, 'url' | 'name' | 'size'>) =>
      portalHtmlPreviewHref(file.url, { downloadName: file.name, sizeBytes: file.size }),
    [],
  );

  const toHtmlSibling = useCallback(
    (file: ProjectFile): HtmlViewerSibling => ({
      documentId: file.id || file.url,
      url: htmlPreviewHref(file),
      fetchUrl: file.url,
      title: file.name,
      versionLabel: file.versionLabel || (file.versionIndex != null ? `#${file.versionIndex}` : undefined),
      projectId,
      projectName: timberProjectName,
      htmlModelDescriptors: file.htmlModelDescriptors,
    }),
    [htmlPreviewHref, projectId, timberProjectName],
  );

  const htmlSiblingModels = useCallback((): HtmlViewerSibling[] => {
    const seen = new Set<string>();
    const out: HtmlViewerSibling[] = [];
    const add = (file: ProjectFile) => {
      if (!isHtmlType(file.mimeType, file.name) || !(file.id || file.url)) return;
      const id = file.id || file.url;
      if (seen.has(id)) return;
      seen.add(id);
      out.push(toHtmlSibling(file));
    };
    streamVersions.forEach(add);
    files.forEach(add);
    return out;
  }, [streamVersions, files, toHtmlSibling]);

  const openHtmlViewer = useCallback(
    (file: ProjectFile) => {
      setHtmlViewerDoc({
        url: htmlPreviewHref(file),
        fetchUrl: file.url,
        title: file.name,
        versionLabel: file.versionLabel,
        documentId: file.id || file.url,
        projectId,
        projectName: timberProjectName,
        streamId: file.streamId || selectedStreamId || undefined,
        siblingModels: htmlSiblingModels(),
        sharedAcrossStructures: fileIsSharedAcrossStructures(file),
      });
    },
    [fileIsSharedAcrossStructures, htmlPreviewHref, htmlSiblingModels, projectId, selectedStreamId, timberProjectName],
  );

  const handleDownload = useCallback(async (file: ProjectFile) => {
    // Use the direct Storage URL for downloads — the /hosted/ proxy is for inline preview only
    // and can return an empty body for large files (Cloud Functions response limits).
    const href = file.url;
    try {
      const resp = await fetch(href);
      if (!resp.ok) throw new Error(String(resp.status));
      const blob = await resp.blob();
      if (blob.size === 0) throw new Error('empty');
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const railGroupLabel = useCallback(
    (streamId: string | undefined) => {
      const group = railGroupForStream(streamId);
      return group && group.structureId !== WHOLE_PROJECT_GROUP_ID ? group.structureName : null;
    },
    [railGroupForStream],
  );
  const trackPrefixLabels = useCallback(
    (stream: ProjectFileStream) => {
      const groupLabel = railGroupLabel(stream.id);
      return [
        ...(groupLabel ? [groupLabel] : []),
        ...(stream.structureGroupLabel ? [stream.structureGroupLabel] : []),
        ...structureNamePrefixLabels(stream.structureNames),
      ];
    },
    [railGroupLabel],
  );

  const commitStreamRename = useCallback(async () => {
    if (!streamRenameId) return;
    const trimmed = streamRenameValue.trim();
    const stream = streams.find(st => st.id === streamRenameId);
    if (!stream) {
      setStreamRenameId(null);
      return;
    }
    if (!trimmed) {
      alert('Name is required.');
      setStreamRenameValue(streamDisplayTitle(stream, railGroupLabel(stream.id)));
      return;
    }
    const nextTitle = titleWithPreservedPrefix(stream.title, trackPrefixLabels(stream), trimmed);
    if (nextTitle === stream.title.trim()) {
      setStreamRenameId(null);
      setStreamRenameValue('');
      return;
    }
    setRenameSaving(true);
    try {
      await renameProjectFileStream(projectId, streamRenameId, nextTitle);
      setStreamRenameId(null);
      setStreamRenameValue('');
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Could not rename version history.');
    } finally {
      setRenameSaving(false);
    }
  }, [streamRenameId, streamRenameValue, streams, projectId, railGroupLabel, trackPrefixLabels]);

  // ── Rename a structure group (rail section heading) ──
  const [groupRenameKey, setGroupRenameKey] = useState<string | null>(null);
  const [groupRenameValue, setGroupRenameValue] = useState('');
  const [groupRenameSaving, setGroupRenameSaving] = useState(false);
  const groupRenameInputRef = useRef<HTMLInputElement>(null);
  const skipGroupRenameBlurRef = useRef(false);
  useEffect(() => {
    if (groupRenameKey) groupRenameInputRef.current?.select();
  }, [groupRenameKey]);
  const startGroupRename = useCallback((group: StreamStructureGroup) => {
    setStreamRenameId(null);
    setGroupRenameKey(group.structureId);
    const names = group.structureNames.filter(Boolean);
    const display =
      group.customLabel || (names.length === 1 ? names[0] : names[0] || group.structureName);
    setGroupRenameValue(display);
  }, []);
  const cancelGroupRename = useCallback(() => {
    setGroupRenameKey(null);
    setGroupRenameValue('');
  }, []);
  const commitGroupRename = useCallback(
    async (group: StreamStructureGroup) => {
      const trimmed = groupRenameValue.trim();
      const names = group.structureNames.filter(Boolean);
      const current =
        group.customLabel || (names.length === 1 ? names[0] : names[0] || group.structureName);
      if (trimmed === current) {
        cancelGroupRename();
        return;
      }
      setGroupRenameSaving(true);
      try {
        // Typing the structures' own name back (or clearing) removes the custom label.
        const auto = structureSelectionLabel(group.structureNames, { maxNamed: 3 });
        const next = !trimmed || trimmed === auto ? '' : trimmed;
        await renameProjectFileStructureGroup(
          projectId,
          group.streams.map(s => s.id || '').filter(Boolean),
          next,
        );
        if (portalId && dealId) {
          void syncPortalApprovalTrailGrouping({ portalId, dealId, projectId }).catch(err => {
            console.warn('[ProjectFiles] portal trail grouping sync failed', err);
          });
        }
        cancelGroupRename();
      } catch (err) {
        console.error(err);
        alert(err instanceof Error ? err.message : 'Could not rename the structure group.');
      } finally {
        setGroupRenameSaving(false);
      }
    },
    [groupRenameValue, projectId, portalId, dealId, cancelGroupRename],
  );

  const beginVersionEdit = useCallback(
    (vf: ProjectFile) => {
      if (!canEditVersionLabel || !vf.id) return;
      setStreamRenameId(null);
      setVersionEdit({
        fileId: vf.id,
        label: vf.versionLabel || '',
        detailStage: vf.detailStage ?? '',
        trueDimensions: vf.trueDimensions === true,
        forCnc: vf.forCnc === true,
        structureIds: vf.cncStructureIds ?? [],
      });
    },
    [canEditVersionLabel],
  );

  const commitVersionEdit = useCallback(async () => {
    if (!canEditVersionLabel || !versionEdit) return;
    const trimmed = versionEdit.label.trim();
    const vf = streamVersions.find(f => f.id === versionEdit.fileId);
    if (!vf?.id) {
      setVersionEdit(null);
      return;
    }
    if (!trimmed) {
      alert('Version name is required.');
      return;
    }
    const isThreeD = streams.find((stream) => stream.id === selectedStreamId)?.streamKind === 'three_d_raw';
    if (isThreeD && versionEdit.forCnc) {
      if (hubStructures.length === 0) {
        alert('This project has no structures, so this version cannot be marked For CNC.');
        return;
      }
      if (versionEdit.structureIds.length === 0) {
        alert('Choose which structures this model is for.');
        return;
      }
    }
    const structureNames = versionEdit.structureIds.map(
      (id) => hubStructures.find((structure) => structure.id === id)?.name || id,
    );
    let cncReleaseFrom: { fileId: string; structureIds: string[] }[] = [];
    if (isThreeD && versionEdit.forCnc) {
      try {
        const claims = (await listCncProjectFiles(projectId))
          .filter((file) => file.forCnc && file.id && file.id !== vf.id)
          .map((file) => ({
            fileId: file.id!,
            fileName: file.name,
            structureIds: file.cncStructureIds ?? [],
            structureNames: file.cncStructureNames ?? [],
          }));
        const conflicts = cncStructureConflicts(claims, versionEdit.structureIds, structureNames);
        if (conflicts.length > 0) {
          const lines = conflicts.map(
            (conflict) => `${conflict.structureName} is already on “${conflict.fileName}”.`,
          );
          const ok = window.confirm(
            `${lines.join('\n')}\n\nSaving will move those structures onto this version.`,
          );
          if (!ok) return;
          const byFile = new Map<string, string[]>();
          for (const conflict of conflicts) {
            const list = byFile.get(conflict.fileId) ?? [];
            list.push(conflict.structureId);
            byFile.set(conflict.fileId, list);
          }
          cncReleaseFrom = Array.from(byFile.entries()).map(([fileId, structureIds]) => ({ fileId, structureIds }));
        }
      } catch (err) {
        console.error(err);
        alert('Could not check which structures already have a CNC model.');
        return;
      }
    }
    setRenameSaving(true);
    try {
      const versionStreamKind = streams.find(s => s.id === (vf.streamId || selectedStreamId))?.streamKind;
      await updateProjectFileVersionEdit(projectId, vf.id, {
        versionLabel: trimmed,
        detailStage: versionEdit.detailStage,
        ...(versionStreamKind === 'timber_list_csv' ? { trueDimensions: versionEdit.trueDimensions } : {}),
        ...(isThreeD
          ? {
              forCnc: versionEdit.forCnc,
              cncStructureIds: versionEdit.forCnc ? versionEdit.structureIds : [],
              cncStructureNames: versionEdit.forCnc ? structureNames : [],
              cncReleaseFrom,
            }
          : {}),
      });
      setVersionEdit(null);
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Could not save this version.');
    } finally {
      setRenameSaving(false);
    }
  }, [canEditVersionLabel, versionEdit, streamVersions, projectId, streams, selectedStreamId, hubStructures]);

  useEffect(() => {
    if (!streamRenameId) return;
    requestAnimationFrame(() => {
      const el = streamRenameAnchor === 'header' ? streamRenameHeaderRef.current : streamRenameSidebarRef.current;
      el?.focus();
      el?.select();
    });
  }, [streamRenameId, streamRenameAnchor]);

  useEffect(() => {
    if (!versionEdit) return;
    requestAnimationFrame(() => {
      versionEditLabelRef.current?.focus();
      versionEditLabelRef.current?.select();
    });
  }, [versionEdit?.fileId]);

  useEffect(() => {
    if (!dealId || partnerReadOnly) {
      setProposalStructures([]);
      return;
    }
    return subscribeProposalStructures(dealId, setProposalStructures, () => setProposalStructures([]));
  }, [dealId, partnerReadOnly]);

  // Heal denormalized stream structureNames when the proposal rename is ahead of Files tags.
  // Staff/PM only — designers cannot read proposals, so they have nothing to sync from.
  useEffect(() => {
    if (!projectId || partnerReadOnly || proposalStructures.length === 0 || streams.length === 0) return;
    const patches = streamStructureNameSyncPatches(streams, proposalStructures);
    if (patches.length === 0) return;
    let cancelled = false;
    void (async () => {
      for (const patch of patches) {
        if (cancelled) return;
        try {
          await updateProjectFileStreamStructures(
            projectId,
            patch.streamId,
            patch.structureIds.map((id, i) => ({ id, name: patch.structureNames[i] || id })),
          );
        } catch (err) {
          console.warn('[ProjectFiles] structure name sync failed', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, partnerReadOnly, proposalStructures, streams]);

  // Structure breakdown exists once the first timber list is on the project and either
  // the proposal lists structures or tracks are already tagged (designer fallback).
  const structuresReady = useMemo(
    () => structuresUnlocked({ streams, files, structures: hubStructures }),
    [streams, files, hubStructures],
  );

  // ── Set up structures (admins / PMs / full employees): one wizard, one commit ──
  // Step 1 creates / fills the proposal structures from the timber-list tracks; step 2 splits
  // the other whole-project tracks into structure groups. Opens straight on step 2 when the
  // proposal already lists structures and there is no timber list left to tie.
  const canManageStructures =
    !partnerReadOnly && !!dealId && canManageProjectFileStructures(user?.role, user?.designations);
  // A structure added after the split, before this date was stored, still starts
  // its timeline at the track's createdAt. Write that down once so every screen agrees.
  useEffect(() => {
    if (!canManageStructures) return undefined;
    const pending = streams.filter(s => {
      if (!s.id || s.inheritVersionsAfter || !s.forkedFromStreamId || !(s.createdAt instanceof Date)) return false;
      const cutoff = effectiveInheritVersionsAfter(s, streams);
      return !!cutoff && cutoff.getTime() === s.createdAt.getTime();
    });
    if (pending.length === 0) return undefined;
    let cancelled = false;
    void (async () => {
      for (const s of pending) {
        if (cancelled || !s.id) return;
        try {
          await stampStreamInheritVersionsAfter(projectId, s.id, s.createdAt);
        } catch (err) {
          console.warn('[ProjectFiles] could not record when this structure was added', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageStructures, projectId, streams]);
  const structureGroups = useMemo(
    () => railGroups.filter(group => group.structureId !== WHOLE_PROJECT_GROUP_ID),
    [railGroups],
  );
  const canEditFileStructures = canManageStructures && structureGroups.length > 0;
  const canSeeFileMoveAudit = user?.role === 'admin';
  const [fileStructureEdit, setFileStructureEdit] = useState<{ fileId: string; groupIds: string[] } | null>(null);
  const [fileStructureSaving, setFileStructureSaving] = useState(false);
  const beginFileStructureEdit = useCallback(
    (file: ProjectFile, inherited: boolean) => {
      if (!file.id || !canEditFileStructures || !canSeeFileMoveAudit) return;
      setVersionEdit(null);
      setStreamRenameId(null);
      const groupIds = structureGroupIdsForFile(file, {
        inherited,
        streams,
        files,
        groupIds: structureGroups.map(group => group.structureId),
      });
      setFileStructureEdit(current =>
        current && current.fileId === file.id ? null : { fileId: file.id!, groupIds },
      );
    },
    [canEditFileStructures, canSeeFileMoveAudit, streams, files, structureGroups],
  );
  const cancelFileStructureEdit = useCallback(() => {
    if (fileStructureSaving) return;
    setFileStructureEdit(null);
  }, [fileStructureSaving]);
  const commitFileStructureEdit = useCallback(async () => {
    if (!fileStructureEdit || fileStructureSaving || !user?.id) return;
    const selected = structureGroups.filter(group => fileStructureEdit.groupIds.includes(group.structureId));
    if (selected.length === 0) {
      alert('Choose at least one structure group.');
      return;
    }
    setFileStructureSaving(true);
    try {
      await assignProjectFileToStructureGroups(
        projectId,
        fileStructureEdit.fileId,
        selected.map(group => ({
          structureIds: group.structureIds,
          structureNames: group.structureNames,
          label: group.structureName,
        })),
        streams,
        files,
        { uid: user.id, name: user.name || user.email || '' },
        { markWholeProject: selected.length === structureGroups.length },
      );
      setFileStructureEdit(null);
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Could not move this file to those structure groups.');
    } finally {
      setFileStructureSaving(false);
    }
  }, [fileStructureEdit, fileStructureSaving, user, structureGroups, projectId, streams, files]);
  const structureSetupTracks = useMemo(() => timberListTracksForStructureSetup(streams), [streams]);
  const splitCandidates = useMemo(() => splitCandidateTracks(visibleStreams), [visibleStreams]);
  const [structuresModalOpen, setStructuresModalOpen] = useState(false);
  const [addStructureOpen, setAddStructureOpen] = useState(false);
  const [moveGroup, setMoveGroup] = useState<StreamStructureGroup | null>(null);
  const [structuresBusy, setStructuresBusy] = useState(false);
  // Which phase the banner is in: structures still missing, or structures ready and tracks to split.
  const structuresPhase: 'structures' | 'split' | null =
    structureSetupTracks.length > 0 && proposalStructures.length < Math.max(2, structureSetupTracks.length)
      ? 'structures'
      : proposalStructures.length >= 2 && splitCandidates.length > 0 && structuresReady
        ? 'split'
        : null;
  const structuresDismissKey = `lctf:filesStructuresDismissed:${projectId}:${structuresPhase ?? 'none'}`;
  const [structuresDismissedKey, setStructuresDismissedKey] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(structuresDismissKey) === '1' ? structuresDismissKey : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    try {
      setStructuresDismissedKey(window.localStorage.getItem(structuresDismissKey) === '1' ? structuresDismissKey : null);
    } catch {
      setStructuresDismissedKey(null);
    }
  }, [structuresDismissKey]);
  const dismissStructuresBanner = useCallback(() => {
    setStructuresDismissedKey(structuresDismissKey);
    try {
      window.localStorage.setItem(structuresDismissKey, '1');
    } catch {
      /* ignore */
    }
  }, [structuresDismissKey]);
  const showStructuresBanner =
    !classicView && canManageStructures && structuresPhase !== null && structuresDismissedKey !== structuresDismissKey;
  const confirmStructureSetup = useCallback(
    async (plan: ProjectStructureSetupPlan) => {
      if (!dealId || !user?.id) return;
      setStructuresBusy(true);
      try {
        const res = await setUpProjectStructures({
          dealId,
          projectId,
          plan,
          streams,
          files,
          structures: proposalStructures,
          actor: { uid: user.id, name: user.name || user.email || '' },
        });
        setStructuresModalOpen(false);
        if (res.focusStreamId) setSelectedStreamId(res.focusStreamId);
        if (res.failures.length) alert(`Structures were set up, but:\n${res.failures.join('\n')}`);
      } catch (err) {
        console.error(err);
        alert(err instanceof Error ? err.message : 'Could not set up structures.');
      } finally {
        setStructuresBusy(false);
      }
    },
    [dealId, projectId, streams, files, proposalStructures, user],
  );
  const confirmAddStructure = useCallback(
    async (input: { row: StructureFromTimberListRow; destination: AddStructureDestination }) => {
      if (!dealId || !user?.id) return;
      setStructuresBusy(true);
      try {
        const res = await addProjectStructureAfterSplit({
          dealId,
          projectId,
          row: input.row,
          destination: input.destination,
          streams,
          files,
          structures: proposalStructures,
          actor: { uid: user.id, name: user.name || user.email || '' },
        });
        if (res.structures.length) {
          setAddStructureOpen(false);
          if (res.focusStreamId) setSelectedStreamId(res.focusStreamId);
        }
        if (res.failures.length) {
          const names = res.structures.map(s => s.name).filter(Boolean).join(', ');
          alert(names ? `${names} was added.\n\n${res.failures.join('\n')}` : res.failures.join('\n'));
        }
      } catch (err) {
        console.error(err);
        alert(err instanceof Error ? err.message : 'Could not add the structure.');
      } finally {
        setStructuresBusy(false);
      }
    },
    [dealId, projectId, streams, files, proposalStructures, user],
  );

  // Project-wide feeds that back the Submittals / RFQs views and the Deliverables status chips.
  // All read-only; derived from existing release collections so nothing already staged changes.
  useEffect(() => {
    if (partnerReadOnly) {
      setDealPortalReleases([]);
      return;
    }
    return subscribeProjectSubmittals(portalId, dealId, setDealPortalReleases);
  }, [portalId, dealId, partnerReadOnly]);

  useEffect(() => {
    if (partnerReadOnly) {
      setProjectHubReleases([]);
      setProjectVendorReleases([]);
      setRfqFeedsReady(true);
      return;
    }
    setRfqFeedsReady(false);
    setRfqFeedError(null);
    let hubReady = false;
    let vendorReady = false;
    const mark = () => {
      if (hubReady && vendorReady) setRfqFeedsReady(true);
    };
    const fail = (label: string, err: Error) => {
      console.warn(`[ProjectFiles] ${label} feed`, err);
      setRfqFeedError(err?.message || `Couldn't load ${label}.`);
    };
    const scope = { dealId, projectId };
    const unsubHub = subscribeProjectHubReleases(
      scope,
      list => {
        setProjectHubReleases(list);
        hubReady = true;
        mark();
      },
      err => {
        fail('supplier releases', err);
        hubReady = true;
        mark();
      },
    );
    const unsubVendor = subscribeProjectVendorReleases(
      scope,
      list => {
        setProjectVendorReleases(list);
        vendorReady = true;
        mark();
      },
      err => {
        fail('vendor releases', err);
        vendorReady = true;
        mark();
      },
    );
    return () => {
      unsubHub();
      unsubVendor();
    };
  }, [projectId, dealId, partnerReadOnly, rfqFeedNonce]);

  useEffect(() => {
    if (!dealId || partnerReadOnly) {
      setRfqPackages([]);
      return;
    }
    return subscribeRfqPackagesForDeal(dealId, setRfqPackages, err => {
      console.warn('[ProjectFiles] rfqPackages feed', err);
      setRfqFeedError(err?.message || "Couldn't load supplier packages.");
    });
  }, [dealId, partnerReadOnly, rfqFeedNonce]);

  // Supplier responses (quotes / not bidding) + approvals — status chips only; a
  // failure here degrades to the plain "Sent" chip rather than blanking the view.
  useEffect(() => {
    if (!dealId || partnerReadOnly) {
      setRfqSubmissions([]);
      setRfqProjectApprovals(new Map());
      return;
    }
    const unsubSubs = subscribeRfqSubmissionsForDeal(dealId, setRfqSubmissions, err => {
      console.warn('[ProjectFiles] rfqSubmissions feed', err);
    });
    const unsubProjects = subscribeRfqProjectApprovalsForDeal(dealId, setRfqProjectApprovals, err => {
      console.warn('[ProjectFiles] rfqProjects approvals feed', err);
    });
    return () => {
      unsubSubs();
      unsubProjects();
    };
  }, [dealId, partnerReadOnly, rfqFeedNonce]);

  useEffect(() => {
    if (!dealId || partnerReadOnly || !rfqRequestsGate.enabled) {
      setRfqRequests([]);
      return;
    }
    return subscribeRfqRequestsForDeal(dealId, setRfqRequests, err => {
      console.warn('[ProjectFiles] rfqRequests feed', err);
      setRfqFeedError(err?.message || "Couldn't load quote requests.");
    });
  }, [dealId, partnerReadOnly, rfqRequestsGate.enabled, rfqFeedNonce]);

  const timberRfqProject = useMemo(
    () => (dealId ? selectRfqProjectForDeal(dealId, 'timber_rfq', rfqProjects) : null),
    [dealId, rfqProjects],
  );
  const cncRfqProject = useMemo(
    () => (dealId ? selectRfqProjectForDeal(dealId, 'cnc_quote', rfqProjects) : null),
    [dealId, rfqProjects],
  );
  const timberProjectForSite = useMemo(() => timberProjects.find(p => p.id === projectId) ?? null, [timberProjects, projectId]);
  const dealForSite = useMemo(() => (dealId ? enhancedDeals.find(d => d.id === dealId) ?? null : null), [enhancedDeals, dealId]);
  const requestCoverageLanes = useMemo(
    () =>
      rfqRequestsGate.enabled
        ? buildRfqCoverage({
            structures: hubStructures,
            streams,
            files,
            requests: rfqRequests,
            packages: rfqPackages,
            hubReleases: projectHubReleases,
            submissions: rfqSubmissions,
            projectById: rfqProjectApprovals,
          })
        : [],
    [rfqRequestsGate.enabled, hubStructures, streams, files, rfqRequests, rfqPackages, projectHubReleases, rfqSubmissions, rfqProjectApprovals],
  );
  // `?request=<ids>` from the Suppliers tab: remember the ids right away (the URL param
  // is dropped), then open the wizard once releases + the pilot gate have loaded.
  useEffect(() => {
    if (!filesInitialRequestReleaseIds) return;
    setPendingRequestReleaseIds(filesInitialRequestReleaseIds);
    onRequestDeepLinkConsumed?.();
  }, [filesInitialRequestReleaseIds, onRequestDeepLinkConsumed]);
  useEffect(() => {
    if (!pendingRequestReleaseIds) return;
    if (!rfqRequestsGate.loaded || !rfqFeedsReady || !streamsReady) return;
    setPendingRequestReleaseIds(null);
    if (!rfqRequestsGate.enabled || !dealId || partnerReadOnly) {
      onDeepLinkError?.('Quote requests are not available here', 'The RFQ requests pilot is off for your account.');
      return;
    }
    if (!classicView) setFilesViewState('rfqs');
    setRequestWizard({ initialStreamId: null, initialReleaseIds: pendingRequestReleaseIds });
  }, [pendingRequestReleaseIds, rfqRequestsGate.loaded, rfqRequestsGate.enabled, rfqFeedsReady, streamsReady, dealId, partnerReadOnly, classicView, onDeepLinkError]);

  // Proposal builder (nodeSets + species selections) for the request wizard's invites.
  const [proposalForRequests, setProposalForRequests] = useState<{
    nodeSets: ProposalNodeSet[];
    species: ProposalSpeciesSelectionSource | null;
  } | null>(null);
  useEffect(() => {
    if (!requestWizard || !dealId) return;
    let cancelled = false;
    void (async () => {
      try {
        const snap = await getDoc(doc(db, 'proposalBuilders', dealId));
        if (cancelled) return;
        if (!snap.exists()) {
          setProposalForRequests({ nodeSets: [], species: null });
          return;
        }
        const data = snap.data() as {
          nodeSets?: ProposalNodeSet[];
          selectedSpecies?: string;
          speciesPricing?: ProposalSpeciesSelectionSource['speciesPricing'];
        };
        setProposalForRequests({
          nodeSets: (data.nodeSets ?? []).filter(ns => ns.enabled !== false),
          species: { selectedSpecies: data.selectedSpecies, speciesPricing: data.speciesPricing },
        });
      } catch (err) {
        console.error('[ProjectFiles] proposal for request wizard', err);
        if (!cancelled) setProposalForRequests({ nodeSets: [], species: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestWizard, dealId]);

  const sendQuoteRequest = useCallback(
    async (draft: RequestTimberQuotesDraft) => {
      if (!dealId || !user?.id) return;
      setRequestBusy(true);
      try {
        const res = await sendRfqRequest(
          {
            ...draft,
            existingProject: timberRfqProject,
            existingReleases: projectHubReleases,
            existingPackages: rfqPackages,
            proposalNodeSets: proposalForRequests?.nodeSets ?? [],
            actor: { uid: user.id, name: user.name || user.email || '' },
          },
          { publishRfqPackage, updateRfqProject },
        );
        setRequestWizard(null);
        setReleaseToast(`Sent ${res.request.label} to ${res.request.supplierOrgIds.length} supplier${res.request.supplierOrgIds.length !== 1 ? 's' : ''}`);
        if (res.failures.length) alert(`The request was sent, but:\n${res.failures.join('\n')}`);
      } catch (err) {
        console.error(err);
        alert(err instanceof Error ? err.message : 'Could not send the quote request.');
      } finally {
        setRequestBusy(false);
      }
    },
    [dealId, user, timberRfqProject, projectHubReleases, rfqPackages, proposalForRequests, publishRfqPackage, updateRfqProject],
  );

  const supplierIdsKey = useMemo(() => {
    const ids = new Set<string>();
    for (const p of rfqPackages) if (p.supplierOrgId) ids.add(p.supplierOrgId);
    for (const r of projectVendorReleases) if (r.supplierOrgId) ids.add(r.supplierOrgId);
    return Array.from(ids).sort().join('|');
  }, [rfqPackages, projectVendorReleases]);
  useEffect(() => {
    if (!supplierIdsKey) {
      setSupplierNames(new Map());
      return;
    }
    let cancelled = false;
    void resolveSupplierOrgNames(supplierIdsKey.split('|')).then(map => {
      if (!cancelled) setSupplierNames(map);
    });
    return () => {
      cancelled = true;
    };
  }, [supplierIdsKey]);

  useEffect(() => {
    if (supplierReadOnly) {
      setDesignerStandards([]);
      return;
    }
    return subscribeDesignerStandards(
      items => {
        setDesignerStandards(items);
        setDesignerStandardsError(null);
      },
      () => {
        setDesignerStandards([]);
        setDesignerStandardsError('Designer Standards are not available for your account.');
      },
    );
  }, [supplierReadOnly]);

  // A deep link to a specific file / track always lands on Deliverables.
  useEffect(() => {
    if (filesDeepLink?.documentId || filesInitialTrackId) setFilesViewState('deliverables');
  }, [filesDeepLink?.documentId, filesInitialTrackId]);

  // `#files-<folder>` comment chips: open a view (and folder) once, then drop the params.
  const [deepLinkFolderPath, setDeepLinkFolderPath] = useState<string | null>(null);
  const viewDeepLinkHandledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!filesInitialView && !filesInitialFolderPath) {
      // Params consumed — reset so the same chip can be clicked again later.
      viewDeepLinkHandledRef.current = null;
      setDeepLinkFolderPath(null);
      return;
    }
    const key = `${filesInitialView ?? ''}|${filesInitialFolderPath ?? ''}`;
    if (viewDeepLinkHandledRef.current === key) return;
    viewDeepLinkHandledRef.current = key;
    const view: ProjectFilesView = filesInitialFolderPath ? 'folders' : filesInitialView || 'deliverables';
    if (!classicView) setFilesViewState(view);
    if (filesInitialFolderPath) setDeepLinkFolderPath(filesInitialFolderPath);
    onViewDeepLinkConsumed?.();
  }, [filesInitialView, filesInitialFolderPath, classicView, onViewDeepLinkConsumed]);

  const filesNotInStreams = useMemo(
    () => {
      if (supplierReadOnly) return [];
      const base = architectReadOnly
        ? files.filter((f) => isArchitectHubVisibleUnversionedFile(f))
        : files.filter(f => !f.streamId && !f.employeeOnly && f.folderPath !== 'financials' && !f.folderPath?.startsWith('financials/'));
      if (!includeFinancials) return base;
      return [...base, ...financialFiles];
    },
    [files, financialFiles, supplierReadOnly, architectReadOnly, includeFinancials],
  );

  const streamStatusById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof summarizeStreamStatus>>();
    if (classicView) return map;
    for (const s of visibleStreams) {
      if (!s.id) continue;
      map.set(
        s.id,
        summarizeStreamStatus({
          stream: s,
          files,
          portalReleases: dealPortalReleases,
          hubReleases: projectHubReleases,
          vendorReleases: projectVendorReleases,
        }),
      );
    }
    return map;
  }, [classicView, visibleStreams, files, dealPortalReleases, projectHubReleases, projectVendorReleases]);

  // ── Split history (hover card + first-visit announcement) ──
  const splitVersionCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of streams) {
      if (!s.id) continue;
      const cutoff = effectiveInheritVersionsAfter(s, streams);
      m.set(s.id, versionsForStream(cutoff ? { ...s, inheritVersionsAfter: cutoff } : s, files).length);
    }
    return m;
  }, [streams, files]);
  // ── Post-split announcement: shown once per user the first time they land here after a split ──
  const splitEvents = useMemo(() => buildSplitHistory(streams, hubStructures), [streams, hubStructures]);
  const currentUid = user?.id ?? null;
  const [splitSeenAt, setSplitSeenAt] = useState<number>(() =>
    currentUid ? readSplitSeenAt(projectId, currentUid) : Number.MAX_SAFE_INTEGER,
  );
  useEffect(() => {
    setSplitSeenAt(currentUid ? readSplitSeenAt(projectId, currentUid) : Number.MAX_SAFE_INTEGER);
  }, [projectId, currentUid]);
  const splitAnnouncement = useMemo(() => {
    const ev = pendingSplitAnnouncement(splitEvents, splitSeenAt);
    // Only announce to people who can actually see the forked tracks (partners included).
    if (!ev || !visibleStreams.some(s => s.forkedFromStreamId)) return null;
    return ev;
  }, [splitEvents, splitSeenAt, visibleStreams]);
  const acknowledgeSplit = useCallback(() => {
    const newest = splitEvents.reduce((m, e) => Math.max(m, e.at.getTime()), 0);
    const at = Math.max(newest, Date.now());
    setSplitSeenAt(at);
    if (currentUid) writeSplitSeenAt(projectId, currentUid, at);
  }, [splitEvents, projectId, currentUid]);

  // ── Move versions between tracks ──
  const [selectMode, setSelectMode] = useState(false);
  const [selectedVersionIds, setSelectedVersionIds] = useState<Set<string>>(() => new Set());
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveBusy, setMoveBusy] = useState(false);
  useEffect(() => {
    setSelectMode(false);
    setSelectedVersionIds(new Set());
  }, [selectedStreamId]);
  const toggleVersionSelected = useCallback((fileId: string) => {
    setSelectedVersionIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }, []);
  const confirmMove = useCallback(
    async (target: MoveVersionsTarget) => {
      if (!user?.id || selectedVersionIds.size === 0) return;
      setMoveBusy(true);
      try {
        const res = await moveProjectFileVersionsToStream(projectId, Array.from(selectedVersionIds), target, {
          uid: user.id,
          name: user.name || user.email || '',
        });
        setMoveModalOpen(false);
        setSelectMode(false);
        setSelectedVersionIds(new Set());
        setSelectedStreamId(res.targetStreamId);
      } catch (err) {
        console.error(err);
        alert(err instanceof Error ? err.message : 'Could not move those versions.');
      } finally {
        setMoveBusy(false);
      }
    },
    [projectId, user, selectedVersionIds],
  );

  const handleMoveToFolder = useCallback(
    async (file: ProjectFile, folderPath: string | null) => {
      if (!file.id) return;
      try {
        await moveUnversionedProjectFile(
          projectId,
          file.id,
          folderPath,
          file.employeeOnly ? 'financials' : 'project',
        );
      } catch (err) {
        console.error(err);
        alert(err instanceof Error ? err.message : 'Could not move this file.');
      }
    },
    [projectId],
  );

  const openStreamFromView = useCallback(
    (streamId: string) => {
      setSelectedStreamId(streamId);
      setFilesView('deliverables');
    },
    [setFilesView],
  );

  const visibleFileCount = useMemo(() => {
    if (supplierReadOnly) return streamVersions.length;
    if (!architectReadOnly) return files.length;
    const streamIds = new Set(visibleStreams.map(s => s.id).filter(Boolean));
    const streamed = files.filter(f => f.streamId && streamIds.has(f.streamId)).length;
    return streamed + (architectReadOnly ? filesNotInStreams.length : 0);
  }, [files, supplierReadOnly, architectReadOnly, visibleStreams, filesNotInStreams.length, streamVersions.length]);

  const openProjectUploadModal = useCallback(
    (initialFiles?: File[], opts?: { folderPath?: string | null }) => {
      const folderPath = opts?.folderPath ?? (filesView === 'folders' ? folderUploadTarget : null);
      openUploadModal({
        initialFiles,
        preferredStreamId: selectedStreamId,
        preferredFolderPath: folderPath,
        preferGeneral: filesView === 'folders',
        onSuccess: ({ lastVersionStreamId }) => {
          if (lastVersionStreamId) {
            setSelectedStreamId(lastVersionStreamId);
            setFilesView('deliverables');
          }
        },
      });
    },
    [openUploadModal, selectedStreamId, filesView, folderUploadTarget, setFilesView],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (partnerReadOnly) return;
      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length === 0) return;
      openProjectUploadModal(dropped);
    },
    [openProjectUploadModal, supplierReadOnly],
  );

  const selectedStream = useMemo(
    () => visibleStreams.find(s => s.id === selectedStreamId) || null,
    [visibleStreams, selectedStreamId]
  );

  useEffect(() => {
    if (!selectedStreamId) {
      setStreamPlanReviews([]);
      return;
    }
    return subscribePlanReviewsForStream(selectedStreamId, setStreamPlanReviews);
  }, [selectedStreamId]);

  const versionPlanReviewsByDocId = useMemo(
    () => buildVersionPlanReviewMap(streamPlanReviews),
    [streamPlanReviews],
  );

  /** Backfill Design group cards for completed reviews that predate the feature. */
  useEffect(() => {
    if (!user?.id || !projectId || streamPlanReviews.length === 0) return;
    for (const review of streamPlanReviews) {
      if (review.status !== 'completed' || !review.id) continue;
      if (backfilledPlanReviewCommentsRef.current.has(review.id)) continue;
      if (taskComments.some((c) => c.planReviewId === review.id)) {
        backfilledPlanReviewCommentsRef.current.add(review.id);
        continue;
      }
      backfilledPlanReviewCommentsRef.current.add(review.id);
      const reviewProjectId = (review.projectId || '').trim();
      if (!reviewProjectId) {
        backfilledPlanReviewCommentsRef.current.add(review.id);
        continue;
      }
      void postPlanReviewDesignGroupComment({
        reviewId: review.id,
        projectId: reviewProjectId,
        authorId: review.completedBy || review.startedBy,
        authorName: review.completedByName || review.startedByName || 'Someone',
        fileName: review.fileName,
        commentCount: review.annotationIds?.length ?? 0,
        taskGroups,
        timberProjects,
        projectTasks,
        projectOnboardings,
      }).catch((e) => {
        console.error('[ProjectFiles] backfill plan review design comment', e);
        backfilledPlanReviewCommentsRef.current.delete(review.id!);
      });
    }
  }, [
    user?.id,
    projectId,
    streamPlanReviews,
    taskComments,
    taskGroups,
    timberProjects,
    projectTasks,
    projectOnboardings,
  ]);

  const canStartPlanReview = canMutateFiles && canManagePlanReview(user?.role);

  const pdfSiblingVersions = useCallback(
    (): PdfViewerSiblingVersion[] =>
      streamVersions
        .filter(f => isPdfType(f.mimeType) && (f.id || f.url))
        .map(f => ({
          documentId: f.id || f.url,
          url: portalHostedAssetHref(f.url, { downloadName: f.name }),
          title: f.name,
          versionLabel: f.versionLabel || (f.versionIndex != null ? `#${f.versionIndex}` : undefined),
        })),
    [streamVersions],
  );

  const openVersionPdf = useCallback(
    (vf: ProjectFile, opts?: { compare?: boolean }) => {
      const siblings = pdfSiblingVersions();
      const currentId = vf.id || vf.url;
      const currentIdx = siblings.findIndex(s => s.documentId === currentId);
      const previous = currentIdx >= 0 ? siblings[currentIdx + 1] : undefined;
      setPdfViewerDoc({
        url: portalHostedAssetHref(vf.url, { downloadName: vf.name }),
        title: vf.name,
        documentId: currentId,
        streamId: vf.streamId || selectedStreamId || undefined,
        siblingVersions: siblings,
        sharedAcrossStructures: fileIsSharedAcrossStructures(vf),
        ...(opts?.compare && previous ? { initialCompareDocumentId: previous.documentId } : {}),
      });
    },
    [fileIsSharedAcrossStructures, selectedStreamId, pdfSiblingVersions],
  );

  const handleReviewVersion = useCallback(
    async (vf: ProjectFile) => {
      if (!user?.id || !vf.id || !isPdfType(vf.mimeType)) return;
      const existing = versionPlanReviewsByDocId.get(vf.id)?.active;
      if (existing) {
        openVersionPdf(vf);
        return;
      }
      if (!canStartPlanReview || planReviewStartingId) return;
      setPlanReviewStartingId(vf.id);
      try {
        await startPlanReview({
          projectId,
          documentId: vf.id,
          documentUrl: vf.url,
          streamId: vf.streamId || selectedStreamId || undefined,
          fileName: vf.name,
          startedBy: user.id,
          startedByName: user.name || user.email || 'Someone',
        });
        openVersionPdf(vf);
      } catch (e) {
        console.error('[ProjectFiles] startPlanReview', e);
        window.alert(e instanceof Error ? e.message : 'Could not start plan review');
      } finally {
        setPlanReviewStartingId(null);
      }
    },
    [
      user,
      versionPlanReviewsByDocId,
      canStartPlanReview,
      planReviewStartingId,
      projectId,
      selectedStreamId,
      openVersionPdf,
    ],
  );

  /** Per-user submission counts for the selected version history (from stored `uploadedBy` on each version). */
  const versionSubmissionStats = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>();
    for (const vf of streamVersions) {
      const uid = vf.uploadedBy?.trim() || '__unknown';
      const name = vf.uploadedByName?.trim() || 'Unknown';
      const prev = map.get(uid);
      map.set(uid, { name, count: (prev?.count ?? 0) + 1 });
    }
    return Array.from(map.entries())
      .map(([userId, v]) => ({ userId, name: v.name, count: v.count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [streamVersions]);

  /** One contributor for this history — omit redundant per-row submitter + duplicate header stats */
  const singleSubmitterInTrack = versionSubmissionStats.length === 1;

  /** Open any hub file in the matching inline viewer (Submittals / RFQs "Open"). */
  const openAnyFile = useCallback(
    (file: ProjectFile) => {
      if (file.streamId) setSelectedStreamId(file.streamId);
      if (isHtmlType(file.mimeType, file.name)) {
        openHtmlViewer(file);
        return;
      }
      if (!openFileViewerForDeepLink(file)) {
        window.open(portalHostedAssetHref(file.url, { downloadName: file.name }), '_blank', 'noopener,noreferrer');
      }
    },
    [openHtmlViewer, openFileViewerForDeepLink],
  );

  // RFQs (quote status and amounts) are for admins, PMs and full employees only.
  const canViewRfqs = !partnerReadOnly && canAccessProjectFilesRfqsView(user?.role, user?.designations);
  const availableViews: ProjectFilesView[] = supplierReadOnly
    ? ['deliverables']
    : architectReadOnly
      ? ['deliverables', 'folders']
      : canViewRfqs
        ? ['deliverables', 'submittals', 'rfqs', 'folders']
        : ['deliverables', 'submittals', 'folders'];
  const effectiveView: ProjectFilesView =
    classicView || !availableViews.includes(filesView) ? 'deliverables' : filesView;
  const showViewSwitcher = !classicView && availableViews.length > 1;
  const submittalCount = dealPortalReleases.length;
  const useRequestsView = canViewRfqs && rfqRequestsGate.enabled;
  const canSendQuoteRequests = useRequestsView && canMutateFiles && !!dealId;
  const rfqCount = useRequestsView
    ? countOpenRequests(rfqRequests, rfqPackages, rfqSubmissions, rfqProjectApprovals)
    : projectHubReleases.length + projectVendorReleases.length;
  const folderFileCount = filesNotInStreams.length;

  const unversionedRowProps = {
    fileHasUnreadFileComment,
    getFileCommentCount,
    onOpenPdf: (file: ProjectFile) =>
      setPdfViewerDoc({
        url: portalHostedAssetHref(file.url, { downloadName: file.name }),
        title: file.name,
        documentId: file.id || file.url,
        streamId: file.streamId,
        sharedAcrossStructures: fileIsSharedAcrossStructures(file),
      }),
    onOpenImage: (file: ProjectFile) =>
      setImageViewerDoc({
        url: portalHostedAssetHref(file.url, { downloadName: file.name }),
        title: file.name,
        documentId: file.id || file.url,
        mimeType: file.mimeType,
        sharedAcrossStructures: fileIsSharedAcrossStructures(file),
      }),
    onOpenCsv: (file: ProjectFile) =>
      setCsvViewerDoc({
        url: portalHostedAssetHref(file.url, { downloadName: file.name }),
        title: file.name,
        documentId: file.id || file.url,
        sharedAcrossStructures: fileIsSharedAcrossStructures(file),
        trueDimensions: viewerTrueDimensions(file),
      }),
    onOpenHtml: openHtmlViewer,
    onDownload: handleDownload,
    onDelete: handleDelete,
    deletingId,
  };

  const canManageDesignerStandards =
    canMutateFiles && canAccessProjectHubClientPortalControls(user?.role, user?.designations);
  const tourScript = resolveProjectFilesTourScript({
    supplierReadOnly,
    architectReadOnly,
    canManageDesignerStandards,
  });
  const { startTour: startFilesTour } = useProjectFilesTour({
    enabled: !loading,
    script: tourScript,
    uid: user?.id,
    classicView,
  });

  return (
    <div
      className={`pf-container ${dragOver ? 'pf-drag-over' : ''}`}
      ref={dropRef}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {/* Upload bar */}
      <div className="pf-toolbar pf-toolbar--wrap">
        {canMutateFiles && (
        <button
          type="button"
          className="pf-upload-btn pf-upload-btn--accent"
          data-tour="pf-upload"
          onClick={() => openProjectUploadModal()}
          disabled={uploading}
        >
          {uploading ? (
            <><Loader2 size={14} className="pf-spin" /> Uploading {uploadProgress}%</>
          ) : (
            <><Upload size={14} /> Upload File</>
          )}
        </button>
        )}
        {showViewSwitcher ? (
          <div className="pf-views" role="tablist" aria-label="Files view" data-tour="pf-views">
            {availableViews.map(v => {
              const label =
                v === 'deliverables' ? 'Deliverables' : v === 'submittals' ? 'Submittals' : v === 'rfqs' ? 'RFQs' : architectReadOnly ? 'Other files' : 'Project folders';
              const Icon = v === 'deliverables' ? Layers : v === 'submittals' ? Users : v === 'rfqs' ? Truck : FolderIcon;
              const count =
                v === 'deliverables'
                  ? visibleStreams.length
                  : v === 'submittals'
                    ? submittalCount
                    : v === 'rfqs'
                      ? rfqCount
                      : folderFileCount;
              return (
                <button
                  key={v}
                  type="button"
                  role="tab"
                  aria-selected={effectiveView === v}
                  className={`pf-view-btn${effectiveView === v ? ' active' : ''}`}
                  onClick={() => setFilesView(v)}
                >
                  <Icon size={12} aria-hidden />
                  {label}
                  {count > 0 ? <span className="pf-view-btn-count">{count}</span> : null}
                </button>
              );
            })}
          </div>
        ) : null}
        <div className="pf-toolbar-right">
          {!partnerReadOnly && redesign.enabled ? (
            <label className="pf-classic-toggle" title="Show the previous single-page Files layout">
              <input type="checkbox" checked={classicViewPref} onChange={toggleClassicView} />
              Classic files view
            </label>
          ) : null}
          {toolbarExtra}
          <span className="pf-file-count">{visibleFileCount} file{visibleFileCount !== 1 ? 's' : ''}</span>
          {/* The help card describes the redesigned views; the classic layout has none of them. */}
          {!classicView ? (
            <FilesHelpButton
              script={tourScript}
              onReplay={startFilesTour}
              onOpenDesignerStandardsSettings={canManageDesignerStandards ? onOpenDesignerStandardsSettings : undefined}
            />
          ) : null}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="pf-loading">
          <Loader2 size={22} className="pf-spin" />
        </div>
      ) : filesError ? (
        <div className="pf-loading">
          <p>{filesError}</p>
          <button type="button" className="ops-new-btn" onClick={() => setFilesRetryNonce(n => n + 1)}>
            Try again
          </button>
        </div>
      ) : effectiveView === 'submittals' ? (
        <div className="pf-content">
          <ProjectSubmittalsView
            projectId={projectId}
            releases={dealPortalReleases}
            files={files}
            streams={streams}
            dealApprovals={dealApprovals}
            portalLinked={!!portalId}
            onOpenSource={openAnyFile}
            onOpenStream={openStreamFromView}
          />
        </div>
      ) : effectiveView === 'rfqs' && useRequestsView ? (
        <div className="pf-content">
          <ProjectRfqRequestsView
            requests={rfqRequests}
            hubReleases={projectHubReleases}
            vendorReleases={projectVendorReleases}
            packages={rfqPackages}
            streams={streams}
            files={files}
            structures={hubStructures}
            supplierNames={supplierNames}
            submissions={rfqSubmissions}
            projectApprovals={rfqProjectApprovals}
            loading={!rfqFeedsReady}
            error={rfqFeedError}
            onRetry={() => setRfqFeedNonce(n => n + 1)}
            onOpenSource={openAnyFile}
            onOpenStream={openStreamFromView}
            onOpenSuppliers={onOpenSuppliers}
            canRequest={canSendQuoteRequests}
            onNewRequest={opts =>
              setRequestWizard({
                initialStreamId: opts?.streamId ?? null,
                initialStreamIds: opts?.streamIds ?? null,
                initialReleaseIds: opts?.releaseIds ?? null,
              })
            }
          />
        </div>
      ) : effectiveView === 'rfqs' ? (
        <div className="pf-content">
          <ProjectRfqFilesView
            hubReleases={projectHubReleases}
            vendorReleases={projectVendorReleases}
            packages={rfqPackages}
            streams={streams}
            files={files}
            structures={hubStructures}
            supplierNames={supplierNames}
            submissions={rfqSubmissions}
            projectApprovals={rfqProjectApprovals}
            loading={!rfqFeedsReady}
            error={rfqFeedError}
            onRetry={() => setRfqFeedNonce(n => n + 1)}
            onOpenSource={openAnyFile}
            onOpenStream={openStreamFromView}
            onOpenSuppliers={onOpenSuppliers}
          />
        </div>
      ) : effectiveView === 'folders' ? (
        <div className="pf-content">
          {folderFilesError ? <p className="pf-empty-hint">{folderFilesError}</p> : null}
          <ProjectFoldersView
            projectId={projectId}
            dealId={dealId}
            files={filesNotInStreams}
            folderTree={folderTree}
            title={architectReadOnly ? 'Other files' : 'Project folders'}
            designerStandards={designerStandards}
            designerStandardsError={designerStandardsError}
            canMutate={canMutateFiles}
            canManageStandards={canManageDesignerStandards}
            onUploadToFolder={folderPath => {
              setFolderUploadTarget(folderPath);
              openProjectUploadModal(undefined, { folderPath });
            }}
            onMoveToFolder={handleMoveToFolder}
            onOpenSettingsStandards={onOpenDesignerStandardsSettings}
            initialFolderPath={tourFolderPath ?? deepLinkFolderPath}
            onSelectedFolderChange={setFolderUploadTarget}
            rowProps={unversionedRowProps}
          />
        </div>
      ) : visibleStreams.length === 0 && !architectReadOnly && (supplierReadOnly || (files.length === 0 && streams.length === 0)) ? (
        <div
          className="pf-empty"
          onClick={() => {
            if (!canMutateFiles || uploading) return;
            openProjectUploadModal();
          }}
        >
          <div className="pf-empty-icon"><Upload size={28} /></div>
          <p className="pf-empty-title">{supplierReadOnly ? 'No drawings or 3D models yet' : 'No files yet'}</p>
          <p className="pf-empty-hint">
            {supplierReadOnly
              ? '2D drawings and 3D models appear here when LC Timber Frames shares a version track for this project.'
              : 'Drag and drop here, or use the Upload File button above for versioned PDF, 3D HTML, timber CSV, or other project files.'}
          </p>
        </div>
      ) : (
        <div className="pf-content">
          {splitAnnouncement ? (
            <SplitAnnouncement
              event={splitAnnouncement}
              currentUid={currentUid}
              onAcknowledge={acknowledgeSplit}
            />
          ) : null}
          {showStructuresBanner ? (
            <div
              className={`pf-split-banner${structuresPhase === 'structures' ? ' pf-split-banner--structures' : ''}`}
              role="status"
              data-tour="pf-split-banner"
            >
              <span className="pf-split-banner-icon" aria-hidden>
                {structuresPhase === 'structures' ? <Layers size={18} /> : <GitFork size={18} />}
              </span>
              <div className="pf-split-banner-text">
                {structuresPhase === 'structures' ? (
                  <>
                    <strong>Timber lists are in — set up the structures</strong>
                    {structureSetupTracks.length} timber-list track{structureSetupTracks.length !== 1 ? 's are' : ' is'} not
                    tied to a structure
                    {proposalStructures.length === 0
                      ? ' and the proposal has no structures yet'
                      : ` (the proposal lists ${proposalStructures.length})`}
                    . Create a structure for each
                    {splitCandidates.length
                      ? `, then decide how the other ${splitCandidates.length} whole-project track${splitCandidates.length !== 1 ? 's' : ''} continue under them — one step, one pass.`
                      : ' so the proposal, deliverables and RFQs can follow per structure.'}
                  </>
                ) : (
                  <>
                    <strong>Structures are ready — split the remaining whole-project tracks</strong>
                    The proposal lists {proposalStructures.length} structure{proposalStructures.length !== 1 ? 's' : ''}.{' '}
                    {splitCandidates.length} track{splitCandidates.length !== 1 ? 's are' : ' is'} still whole-project (
                    {splitCandidates
                      .slice(0, 4)
                      .map(s => `${trackKindShort(s.streamKind)} ${s.title}`)
                      .join(', ')}
                    {splitCandidates.length > 4 ? `, +${splitCandidates.length - 4} more` : ''}
                    ) and can continue per structure or per group of structures.
                  </>
                )}
              </div>
              <div className="pf-split-banner-acts">
                <button type="button" className="pf-version-act pf-version-act--muted" onClick={dismissStructuresBanner}>
                  Not now
                </button>
                <button type="button" className="pf-version-act" onClick={() => setStructuresModalOpen(true)}>
                  {structuresPhase === 'structures' ? 'Set up structures…' : 'Split tracks…'}
                </button>
              </div>
            </div>
          ) : null}
          {visibleStreams.length > 0 && (
            <div
              className={`pf-streams-layout${
                classicView && !partnerReadOnly && filesNotInStreams.length > 0 ? ' pf-streams-layout--sep' : ''
              }`}
            >
              <aside className="pf-streams-sidebar" data-tour="pf-structure-rail">
                {classicView ? (
                  <div className="pf-section-title-row">
                    <h3 className="pf-section-title">
                      <Layers size={14} /> Version history
                    </h3>
                    {canManageStructures && structureGroups.length > 0 ? (
                      <button
                        type="button"
                        className="pf-version-act pf-add-structure-btn"
                        onClick={() => setAddStructureOpen(true)}
                      >
                        <Plus size={12} aria-hidden /> Add structure…
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <div className="pf-section-title-row">
                    <SplitHistoryPopover streams={streams} structures={hubStructures} versionCounts={splitVersionCounts}>
                      <h3 className="pf-section-title">
                        <Layers size={14} /> Deliverables
                      </h3>
                    </SplitHistoryPopover>
                    {canManageStructures && structureGroups.length > 0 ? (
                      <button
                        type="button"
                        className="pf-version-act pf-add-structure-btn"
                        onClick={() => setAddStructureOpen(true)}
                      >
                        <Plus size={12} aria-hidden /> Add structure…
                      </button>
                    ) : null}
                  </div>
                )}
                {(() => {
                  const renderStreamRow = (s: ProjectFileStream, rowIndex: number, groupLabel?: string | null) => {
                    const addedCutoff = effectiveInheritVersionsAfter(s, streams);
                    const head = headVersionForStream(
                      addedCutoff ? { ...s, inheritVersionsAfter: addedCutoff } : s,
                      files,
                    );
                    const status = s.id ? streamStatusById.get(s.id) : undefined;
                    const shortTitle = groupLabel ? streamDisplayTitle(s, groupLabel) : s.title;
                    // LOD, portal, and staged stay on the type badge (HTML, 2D, …) until hover.
                    type RailChip = { key: string; label: string; className: string; title: string };
                    const kindHoverChips: RailChip[] = [];
                    if (status?.headLodLabel) {
                      kindHoverChips.push({
                        key: 'lod',
                        label: status.headLodLabel,
                        className: 'pfv-chip--lod',
                        title: 'Level of detail of the latest version',
                      });
                    }
                    if (status) {
                      if (status.headReleased) {
                        kindHoverChips.push({ key: 'portal', label: 'On portal', className: 'pfv-chip--current', title: 'Latest version is on the client portal' });
                      } else if (status.headAheadOfPortal) {
                        kindHoverChips.push({ key: 'portal', label: 'Portal behind', className: 'pfv-chip--ahead', title: 'A newer version exists than the one on the client portal' });
                      }
                      if (status.stagedVersions > 0) {
                        kindHoverChips.push({
                          key: 'staged',
                          label: `Staged ×${status.stagedVersions}`,
                          className: status.headStaged ? 'pfv-chip--current' : '',
                          title: `${status.stagedVersions} version${status.stagedVersions !== 1 ? 's' : ''} staged for suppliers`,
                        });
                      }
                    }
                    const renamingRail = !!s.id && streamRenameId === s.id && streamRenameAnchor === 'rail';
                    const rowClass = `pf-stream-row${selectedStreamId === s.id ? ' pf-stream-row--active' : ''}`;
                    const rowTitle =
                      [
                        shortTitle !== s.title ? s.title : null,
                        head ? `Latest: ${head.versionLabel || 'version'}${head.versionIndex != null ? ` #${head.versionIndex}` : ''} · ${head.uploadedByName || 'Unknown'}` : null,
                        ...kindHoverChips.map(c => `${c.label} — ${c.title}`),
                      ].filter(Boolean).join('\n') || undefined;
                    const rowBody = (
                      <>
                          <span className="pf-stream-kind-col" aria-hidden>
                            <span
                              className={`pf-stream-kind-badge pf-stream-kind-badge--col pf-stream-kind-${s.streamKind}`}
                              data-tour={rowIndex === 0 ? 'pf-status-chips' : undefined}
                            >
                              {trackKindShort(s.streamKind)}
                            </span>
                            {kindHoverChips.length > 0 ? (
                              <span className="pf-stream-kind-flyout">
                                {kindHoverChips.map(c => (
                                  <span key={c.key} className={`pfv-chip${c.className ? ` ${c.className}` : ''}`} title={c.title}>
                                    {c.label}
                                  </span>
                                ))}
                              </span>
                            ) : null}
                          </span>
                          <div className="pf-stream-row-main">
                            {renamingRail ? (
                              <input
                                ref={streamRenameSidebarRef}
                                className="pf-stream-row-title pf-inline-rename-input"
                                value={streamRenameValue}
                                disabled={renameSaving}
                                aria-label="Version history name"
                                onChange={e => setStreamRenameValue(e.target.value)}
                                onClick={e => e.stopPropagation()}
                                onMouseDown={e => e.stopPropagation()}
                                onKeyDown={e => {
                                  e.stopPropagation();
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void commitStreamRename();
                                  }
                                  if (e.key === 'Escape') {
                                    e.preventDefault();
                                    skipStreamRenameBlurRef.current = true;
                                    setStreamRenameId(null);
                                    setStreamRenameValue('');
                                  }
                                }}
                                onBlur={() => {
                                  if (skipStreamRenameBlurRef.current) {
                                    skipStreamRenameBlurRef.current = false;
                                    return;
                                  }
                                  void commitStreamRename();
                                }}
                              />
                            ) : (
                              <span
                                className="pf-stream-row-title pf-editable-label"
                                title={canMutateFiles ? 'Double-click to rename' : undefined}
                                onDoubleClick={e => {
                                  if (!canMutateFiles) return;
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!s.id) return;
                                  setVersionEdit(null);
                                  setGroupRenameKey(null);
                                  setStreamRenameAnchor('rail');
                                  setStreamRenameId(s.id);
                                  setStreamRenameValue(shortTitle);
                                }}
                              >
                                {shortTitle}
                              </span>
                            )}
                            <span className="pf-sr-only">{trackKindShort(s.streamKind)} track</span>
                            {s.streamRole === 'supplementary' ? (
                              <span className="pf-stream-supplementary-badge">Supplementary</span>
                            ) : null}
                            {head?.trueDimensions ? (
                              <span
                                className="pf-version-true-dim"
                                title="True timber dimensions. Cross-section stays as written in the CSV (7.5 stays 7.5)."
                              >
                                True dimensions
                              </span>
                            ) : null}
                          </div>
                          <div className="pf-stream-row-meta">
                            {head ? (
                              <>
                                <span className="pf-stream-meta-user">{head.uploadedByName || 'Unknown'}</span>
                                {head.versionIndex != null ? ` · #${head.versionIndex}` : ''}
                                {' · '}
                                {formatDistanceToNow(head.uploadedAt, { addSuffix: true })}
                              </>
                            ) : !addedCutoff && s.headFileId && s.lastSubmittedByName ? (
                              <span className="pf-stream-meta-user">{s.lastSubmittedByName}</span>
                            ) : (
                              'No versions yet'
                            )}
                          </div>
                          <ChevronRight size={14} className="pf-stream-chevron" aria-hidden />
                      </>
                    );
                    return (
                      <li key={s.id} className="pf-stream-row-li" data-tour={rowIndex === 0 ? 'pf-stream-row' : undefined}>
                        <div className="pf-stream-row-bar">
                        {renamingRail ? (
                          <div className={rowClass} title={rowTitle}>
                            {rowBody}
                          </div>
                        ) : (
                          <button
                            type="button"
                            className={rowClass}
                            title={rowTitle}
                            onClick={() => setSelectedStreamId(s.id || null)}
                          >
                            {rowBody}
                          </button>
                        )}
                        {canMutateFiles && isVersionTrackWithNoFiles(s) && !s.forkedFromStreamId && (
                          <button
                            type="button"
                            className="pf-stream-delete-btn"
                            title="Delete empty version history"
                            aria-label={`Delete empty version history “${s.title}”`}
                            disabled={deletingStreamId === s.id}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              void handleDeleteEmptyTrack(s);
                            }}
                          >
                            {deletingStreamId === s.id ? (
                              <Loader2 size={14} className="pf-spin" aria-hidden />
                            ) : (
                              <Trash2 size={14} aria-hidden />
                            )}
                          </button>
                        )}
                        </div>
                      </li>
                    );
                  };
                  if (!railSections) {
                    return <ul className="pf-stream-list">{railStreams.map((s, i) => renderStreamRow(s, i))}</ul>;
                  }
                  let rowCounter = 0;
                  return railSections.map(group => {
                    const isUntagged = group.structureId === WHOLE_PROJECT_GROUP_ID;
                    const setNames = isUntagged ? [] : group.structureNames.filter(Boolean);
                    const groupDisplayName = isUntagged
                      ? WHOLE_PROJECT_GROUP_LABEL
                      : group.customLabel ||
                        (setNames.length === 1 ? setNames[0] : setNames[0] || group.structureName);
                    const showStructureList = !isUntagged && setNames.length > 0;
                    const renaming = groupRenameKey === group.structureId;
                    const canRenameGroup = canMutateFiles && !isUntagged;
                    return (
                      <section
                        key={group.structureId}
                        className={`pf-structure-section${isUntagged ? ' pf-structure-section--untagged' : ''}${classicView ? ' pf-structure-section--classic' : ''}`}
                      >
                        <h4
                          className={`pf-structure-heading${isUntagged ? ' pf-structure-heading--untagged' : ''}${canRenameGroup ? ' pf-structure-heading--editable' : ''}`}
                          title={
                            renaming
                              ? undefined
                              : isUntagged
                                ? 'Tracks not tagged to a structure'
                                : [group.structureTitle, canRenameGroup ? 'Double-click the group name to rename' : null]
                                    .filter(Boolean)
                                    .join('\n') || undefined
                          }
                        >
                          <div
                            className="pf-structure-heading-band"
                            onDoubleClick={e => {
                              if (!canRenameGroup || renaming) return;
                              e.preventDefault();
                              startGroupRename(group);
                            }}
                          >
                            <div className="pf-structure-heading-top">
                              {renaming ? (
                                <input
                                  ref={groupRenameInputRef}
                                  className="pf-structure-heading-input"
                                  value={groupRenameValue}
                                  disabled={groupRenameSaving}
                                  aria-label="Structure group name"
                                  placeholder={structureSelectionLabel(setNames, { maxNamed: 3 })}
                                  onChange={e => setGroupRenameValue(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                  onKeyDown={e => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      void commitGroupRename(group);
                                    }
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      skipGroupRenameBlurRef.current = true;
                                      cancelGroupRename();
                                    }
                                  }}
                                  onBlur={() => {
                                    if (skipGroupRenameBlurRef.current) {
                                      skipGroupRenameBlurRef.current = false;
                                      return;
                                    }
                                    void commitGroupRename(group);
                                  }}
                                />
                              ) : (
                                <span className="pf-structure-heading-label">{groupDisplayName}</span>
                              )}
                              <span className="pf-structure-heading-actions">
                                {canRenameGroup && !renaming ? (
                                  <button
                                    type="button"
                                    className="pf-structure-heading-edit"
                                    aria-label={`Rename group “${groupDisplayName}”`}
                                    title="Rename group"
                                    onClick={e => {
                                      e.stopPropagation();
                                      startGroupRename(group);
                                    }}
                                  >
                                    <Pencil size={11} aria-hidden />
                                  </button>
                                ) : null}
                                {canManageStructures && !isUntagged && !renaming ? (
                                  <button
                                    type="button"
                                    className="pf-structure-heading-edit"
                                    aria-label={`Move group “${groupDisplayName}” to another project`}
                                    title="Move this group to another project"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setMoveGroup(group);
                                    }}
                                  >
                                    <MoveRight size={11} aria-hidden />
                                  </button>
                                ) : null}
                                <span className="pf-structure-heading-count">{group.streams.length}</span>
                              </span>
                            </div>
                            {showStructureList && !renaming ? (
                              <p className="pf-structure-heading-structures">{setNames.join(' · ')}</p>
                            ) : null}
                            {!isUntagged && !renaming ? (() => {
                              const addedAt = group.streams.reduce<Date | null>((earliest, stream) => {
                                const at = effectiveInheritVersionsAfter(stream, streams);
                                if (!at) return earliest;
                                if (!earliest || at.getTime() < earliest.getTime()) return at;
                                return earliest;
                              }, null);
                              return addedAt ? (
                                <p className="pf-structure-heading-structures">Added {format(addedAt, 'MMM d, yyyy')}</p>
                              ) : null;
                            })() : null}
                          </div>
                        </h4>
                        <ul className="pf-stream-list">
                          {group.streams.map(s => renderStreamRow(s, rowCounter++, isUntagged ? null : group.structureName))}
                        </ul>
                      </section>
                    );
                  });
                })()}
              </aside>
              <div className="pf-stream-main" data-tour="pf-lod-timeline">
                {selectedStream ? (
                  <>
                    <div className="pf-timeline-header">
                      <div className="pf-timeline-heading-row">
                        {(() => {
                          const group = railGroupForStream(selectedStream.id);
                          if (!group) return null;
                          const isWhole = group.structureId === WHOLE_PROJECT_GROUP_ID;
                          return (
                            <span
                              className={`pf-timeline-crumb${isWhole ? ' pf-timeline-crumb--whole' : ''}`}
                              title={isWhole ? 'Tracks not tagged to a structure' : group.structureTitle}
                            >
                              {isWhole ? <Layers size={11} aria-hidden /> : <Tag size={11} aria-hidden />}
                              <span className="pf-timeline-crumb-label">{isWhole ? WHOLE_PROJECT_GROUP_LABEL : group.structureName}</span>
                              <ChevronRight size={12} className="pf-timeline-crumb-sep" aria-hidden />
                            </span>
                          );
                        })()}
                        {selectedStream &&
                        selectedStream.id &&
                        streamRenameAnchor === 'header' &&
                        streamRenameId === selectedStream.id ? (
                          <input
                            ref={streamRenameHeaderRef}
                            className="pf-timeline-title pf-inline-rename-input pf-inline-rename-input--header"
                            value={streamRenameValue}
                            disabled={renameSaving}
                            aria-label="Version history name"
                            onChange={e => setStreamRenameValue(e.target.value)}
                            onKeyDown={e => {
                              e.stopPropagation();
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void commitStreamRename();
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                skipStreamRenameBlurRef.current = true;
                                setStreamRenameId(null);
                                setStreamRenameValue('');
                              }
                            }}
                            onBlur={() => {
                              if (skipStreamRenameBlurRef.current) {
                                skipStreamRenameBlurRef.current = false;
                                return;
                              }
                              void commitStreamRename();
                            }}
                          />
                        ) : selectedStream ? (
                          (() => {
                            const group = railGroupForStream(selectedStream.id);
                            const groupLabel = group && group.structureId !== WHOLE_PROJECT_GROUP_ID ? group.structureName : null;
                            const shown = groupLabel ? streamDisplayTitle(selectedStream, groupLabel) : selectedStream.title;
                            const tip = [shown !== selectedStream.title ? selectedStream.title : null, canMutateFiles ? 'Double-click to rename' : null]
                              .filter(Boolean)
                              .join('\n');
                            return (
                              <div className="pf-timeline-title-wrap">
                                <h2
                                  className={`pf-timeline-title${canMutateFiles ? ' pf-editable-label' : ''}`}
                                  title={tip || undefined}
                                  onDoubleClick={() => {
                                    if (!canMutateFiles || !selectedStream.id) return;
                                    setVersionEdit(null);
                                    setFileStructureEdit(null);
                                    setGroupRenameKey(null);
                                    setStreamRenameAnchor('header');
                                    setStreamRenameId(selectedStream.id);
                                    setStreamRenameValue(shown);
                                  }}
                                >
                                  {shown}
                                </h2>
                              </div>
                            );
                          })()
                        ) : null}
                        <span className="pf-timeline-count">
                          {streamVersions.length} version{streamVersions.length !== 1 ? 's' : ''} · newest first
                        </span>
                        <div className="pf-timeline-actions">
                        {canReleaseToVendor &&
                          selectedStream &&
                          isVendorReleasableStreamKind(selectedStream.streamKind) &&
                          streamVersions.length > 0 && (
                            <button
                              type="button"
                              className="pf-release-track-btn pf-release-vendor-btn"
                              onClick={() => {
                                if (!selectedStream.id) return;
                                setVendorReleaseModalState({
                                  streamId: selectedStream.id,
                                  streamTitle: selectedStream.title,
                                  initialVersionId: null,
                                });
                              }}
                              title="Release an immutable copy to CNC vendors on this work order"
                            >
                              <Send size={12} aria-hidden />
                              Release to vendor…
                            </button>
                          )}
                        {canSendQuoteRequests &&
                          selectedStream?.id &&
                          selectedStream.streamKind === 'timber_list_csv' &&
                          !selectedStream.wholeProjectRoot &&
                          streamVersions.length > 0 && (
                            <button
                              type="button"
                              className="pf-release-track-btn"
                              onClick={() => setRequestWizard({ initialStreamId: selectedStream.id ?? null })}
                              title="Ask timber suppliers to quote this list (opens the request wizard with this track selected)"
                            >
                              <Send size={12} aria-hidden />
                              Request quotes…
                            </button>
                          )}
                        {canManageStructures &&
                          selectedStream?.id &&
                          (structureSetupTracks.some(s => s.id === selectedStream.id) ||
                            (proposalStructures.length >= 2 && splitCandidates.some(s => s.id === selectedStream.id))) && (
                            <button
                              type="button"
                              className="pf-release-track-btn"
                              onClick={() => setStructuresModalOpen(true)}
                              title={
                                structureSetupTracks.some(s => s.id === selectedStream.id)
                                  ? 'Create the proposal structures from the timber lists and split the other whole-project tracks under them'
                                  : 'Split this whole-project track (and the others) into structure groups'
                              }
                            >
                              {structureSetupTracks.some(s => s.id === selectedStream.id) ? (
                                <Layers size={12} aria-hidden />
                              ) : (
                                <GitFork size={12} aria-hidden />
                              )}
                              {structureSetupTracks.some(s => s.id === selectedStream.id) ? 'Set up structures…' : 'Split tracks…'}
                            </button>
                          )}
                        {canManageStructures &&
                          canMutateFiles &&
                          selectedStream &&
                          !selectedStream.wholeProjectRoot &&
                          streamVersions.length > 0 && (
                            <button
                              type="button"
                              className={`pf-release-track-btn pf-select-versions-btn${selectMode ? ' is-on' : ''}`}
                              aria-pressed={selectMode}
                              onClick={() => {
                                setSelectMode(on => !on);
                                setSelectedVersionIds(new Set());
                              }}
                              title={
                                selectMode
                                  ? 'Stop selecting versions'
                                  : 'Pick versions to move onto another track — e.g. a structure track'
                              }
                            >
                              <MoveRight size={12} aria-hidden />
                              {selectMode ? 'Done selecting' : 'Select versions'}
                            </button>
                          )}
                        {streamVersions.length > 0 && versionStageTree.stages.length > 0 ? (
                          <div className="pfv-seg pf-version-layout-seg" role="group" aria-label="Version layout">
                            <button
                              type="button"
                              className={`pfv-seg-btn${versionLayout === 'lod' ? ' pfv-seg-btn--on' : ''}`}
                              aria-pressed={versionLayout === 'lod'}
                              title="Group versions by level of detail"
                              onClick={() => setVersionLayout('lod')}
                            >
                              By LOD
                            </button>
                            <button
                              type="button"
                              className={`pfv-seg-btn${versionLayout === 'timeline' ? ' pfv-seg-btn--on' : ''}`}
                              aria-pressed={versionLayout === 'timeline'}
                              title="One flat timeline, newest first"
                              onClick={() => setVersionLayout('timeline')}
                            >
                              Timeline
                            </button>
                          </div>
                        ) : null}
                        </div>
                      </div>
                      {selectMode ? (
                        <div className="pf-select-bar" role="status">
                          <strong>{selectedVersionIds.size}</strong> selected — tick the versions to move, then choose a destination.
                          <button
                            type="button"
                            className="pf-version-act"
                            disabled={selectedVersionIds.size === 0}
                            onClick={() => setMoveModalOpen(true)}
                          >
                            Move to another track…
                          </button>
                        </div>
                      ) : null}
                      {!singleSubmitterInTrack && versionSubmissionStats.length > 0 && (
                        <p className="pf-submission-stats" title="Count of file submissions per team member in this history">
                          Submissions by user:{' '}
                          {versionSubmissionStats.map((row, i) => (
                            <React.Fragment key={row.userId}>
                              {i > 0 ? ' · ' : ''}
                              <strong>{row.name}</strong> ({row.count})
                            </React.Fragment>
                          ))}
                        </p>
                      )}
                    </div>
                    {!streamVersionsReady ? (
                      <div className="pf-timeline-empty">Loading versions…</div>
                    ) : streamVersions.length === 0 ? (
                      <div className="pf-timeline-empty pf-timeline-empty--stack">
                        <p>
                          {selectedInheritAfter
                            ? `No versions yet. Added ${format(selectedInheritAfter, 'MMM d, yyyy')}. Files uploaded after that show up here.`
                            : 'No versions uploaded yet.'}
                        </p>
                        {canMutateFiles && selectedStream && isVersionTrackWithNoFiles(selectedStream) && !selectedStream.forkedFromStreamId && (
                          <button
                            type="button"
                            className="pf-empty-track-delete"
                            onClick={() => void handleDeleteEmptyTrack(selectedStream)}
                            disabled={deletingStreamId === selectedStream.id}
                          >
                            {deletingStreamId === selectedStream.id ? (
                              <>
                                <Loader2 size={13} className="pf-spin" aria-hidden /> Removing…
                              </>
                            ) : (
                              <>Remove empty history</>
                            )}
                          </button>
                        )}
                      </div>
                    ) : (
                      (() => {
                        const renderVersion = (vf: ProjectFile) => {
                          // Pre-split history shown on a structure track lives on the root. LOD edits
                          // write that same document, so they show on every structure track.
                          const inherited = isInheritedVersion(vf, selectedStream);
                          const sharedBadge = sharedStructureBadge(vf, streams, selectedStream?.id);
                          const trackHead = headVersionForStream(
                            selectedInheritAfter && selectedStream
                              ? { ...selectedStream, inheritVersionsAfter: selectedInheritAfter }
                              : selectedStream,
                            files,
                          );
                          const isLatest = trackHead ? vf.id === trackHead.id : !!vf.isHeadVersion;
                          // Inherited rows were released from the root track, so look them up deal-wide.
                          const versionReleases =
                            (vf.id ? releasesByFileId.get(vf.id) : undefined) ??
                            (inherited && vf.id ? dealReleasesByFileId.get(vf.id) : undefined) ??
                            (vf.wholeProjectOrigin ? dealReleasesByFileId.get(vf.wholeProjectOrigin.fileId) : undefined);
                          const latestRelease = versionReleases?.[0];
                          const versionHubReleases = vf.id ? hubReleasesByFileId.get(vf.id) : undefined;
                          const latestHubRelease = versionHubReleases?.[0];
                          const clientApproval = versionReleases?.length
                            ? findClientApprovalForFileReleases(dealApprovals, versionReleases)
                            : undefined;
                          const versionUnread = fileHasUnreadFileComment(vf);
                          const versionCommentCount = getFileCommentCount(vf);
                          const versionOpensInline =
                            isPdfType(vf.mimeType) ||
                            isImageType(vf.mimeType) ||
                            isCsvType(vf.mimeType, vf.name);
                          const versionReviewState = vf.id
                            ? versionPlanReviewsByDocId.get(vf.id)
                            : undefined;
                          const activeVersionReview = versionReviewState?.active ?? null;
                          const completedVersionReview = versionReviewState?.latestCompleted ?? null;
                          const openVersionInline = () => {
                            if (isPdfType(vf.mimeType)) {
                              openVersionPdf(vf);
                            } else if (isImageType(vf.mimeType)) {
                              setImageViewerDoc({
                                url: portalHostedAssetHref(vf.url, { downloadName: vf.name }),
                                title: vf.name,
                                documentId: vf.id || vf.url,
                                mimeType: vf.mimeType,
                                sharedAcrossStructures: fileIsSharedAcrossStructures(vf),
                              });
                            } else if (isCsvType(vf.mimeType, vf.name)) {
                              setCsvViewerDoc({
                                url: portalHostedAssetHref(vf.url, { downloadName: vf.name }),
                                title: vf.name,
                                documentId: vf.id || vf.url,
                                sharedAcrossStructures: fileIsSharedAcrossStructures(vf),
                                trueDimensions: viewerTrueDimensions(vf),
                              });
                            }
                          };
                          return (
                          <li
                            key={vf.id}
                            className={`pf-version-node${versionUnread ? ' pf-version-node--unread' : ''}${fileStructureEdit?.fileId === vf.id ? ' pf-version-node--structure-open' : ''}`}
                          >
                            <div className="pf-version-rail" aria-hidden>
                              <span className="pf-version-dot" />
                            </div>
                            <div className="pf-version-sheet">
                              <div className="pf-version-sheet-top">
                                <div className="pf-version-titleblock">
                                  {selectMode && vf.id && !inherited ? (
                                    <input
                                      type="checkbox"
                                      className="pf-version-select"
                                      aria-label={`Select ${vf.versionLabel || 'version'} #${vf.versionIndex ?? ''}`}
                                      checked={selectedVersionIds.has(vf.id)}
                                      onChange={() => toggleVersionSelected(vf.id!)}
                                    />
                                  ) : null}
                                  <span
                                    className={`pf-version-title${canEditVersionLabel ? ' pf-editable-label' : ''}`}
                                    title={canEditVersionLabel ? 'Double-click to edit' : undefined}
                                    onDoubleClick={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      beginVersionEdit(vf);
                                    }}
                                  >
                                    {vf.versionLabel || 'Version'}
                                  </span>
                                  {vf.versionIndex != null ? (
                                    <span className="pf-version-rev">{`#${vf.versionIndex}`}</span>
                                  ) : null}
                                  {isLatest ? (
                                    <span className="pf-version-current">Latest</span>
                                  ) : null}
                                  {inherited || vf.wholeProjectOrigin || vf.uploadedAsWholeProject ? (
                                    <span
                                      className="pf-chip-whole"
                                      title={
                                        vf.uploadedAsWholeProject && !inherited
                                          ? 'Uploaded as Whole project — a copy on each structure group’s track of this type'
                                          : vf.uploadedAsWholeProject
                                            ? 'Whole project — one file shared by every structure. Comments, markup, and edits stay together.'
                                            : 'Uploaded before the structure split — one file shared by every structure track. Comments, markup, and edits stay together.'
                                      }
                                    >
                                      <GitFork size={10} aria-hidden /> Whole project
                                    </span>
                                  ) : null}
                                  {sharedBadge ? (
                                    <span className="pf-chip-shared" title={sharedBadge.title}>
                                      <Share2 size={10} aria-hidden /> {sharedBadge.label}
                                    </span>
                                  ) : null}
                                  {canSeeFileMoveAudit && canEditFileStructures && vf.id ? (
                                    <button
                                      type="button"
                                      className={`pf-version-structure-btn${fileStructureEdit?.fileId === vf.id ? ' is-on' : ''}`}
                                      aria-label={`Structure groups for ${vf.versionLabel || vf.name || 'this version'}`}
                                      aria-expanded={fileStructureEdit?.fileId === vf.id}
                                      title="Move this version to structure groups"
                                      onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        beginFileStructureEdit(vf, inherited);
                                      }}
                                    >
                                      <Pencil size={12} aria-hidden />
                                    </button>
                                  ) : null}
                                  {canSeeFileMoveAudit && vf.moveHistory?.length ? (
                                    <span
                                      className="pf-chip-moved"
                                      title={vf.moveHistory
                                        .map(h => `Moved from ${h.fromStreamTitle || 'another track'} #${h.fromVersionIndex} by ${h.movedByName || 'staff'} ${formatDistanceToNow(h.movedAt, { addSuffix: true })}`)
                                        .join('\n')}
                                    >
                                      moved from {vf.moveHistory[vf.moveHistory.length - 1].fromStreamTitle || 'another track'} #
                                      {vf.moveHistory[vf.moveHistory.length - 1].fromVersionIndex}
                                    </span>
                                  ) : null}
                                  {vf.trueDimensions ? (
                                    <span
                                      className="pf-version-true-dim"
                                      title="True timber dimensions. Cross-section stays as written in the CSV (7.5 stays 7.5)."
                                    >
                                      True dimensions
                                    </span>
                                  ) : null}
                                  {vf.forCnc && (vf.cncStructureNames?.length || vf.cncStructureIds?.length) ? (
                                    <span
                                      className="pf-version-cnc"
                                      title={`CNC model for ${(vf.cncStructureNames ?? []).join(', ')}`}
                                    >
                                      CNC
                                      {(vf.cncStructureNames ?? []).length > 0
                                        ? ` · ${(vf.cncStructureNames ?? []).join(', ')}`
                                        : ''}
                                    </span>
                                  ) : null}
                                  {activeVersionReview ? (
                                    <span
                                      className="pf-version-reviewed pf-version-reviewed--active"
                                      title={`Plan review started by ${activeVersionReview.startedByName} ${formatDistanceToNow(activeVersionReview.startedAt, { addSuffix: true })}`}
                                    >
                                      In review
                                    </span>
                                  ) : null}
                                  {latestRelease ? (
                                    <span
                                      className="pf-version-released"
                                      title={
                                        versionReleases && versionReleases.length > 1
                                          ? `Released ${versionReleases.length}× to client portal — latest as v${latestRelease.releaseVersionIndex} ${formatDistanceToNow(latestRelease.releasedAt, { addSuffix: true })}`
                                          : `Released to client portal as v${latestRelease.releaseVersionIndex} ${formatDistanceToNow(latestRelease.releasedAt, { addSuffix: true })}`
                                      }
                                      aria-label={`Released to client portal as v${latestRelease.releaseVersionIndex} ${formatDistanceToNow(latestRelease.releasedAt, { addSuffix: true })}`}
                                    >
                                      <CheckCircle2 size={11} aria-hidden />
                                      Released v{latestRelease.releaseVersionIndex}
                                    </span>
                                  ) : null}
                                  {clientApproval ? (
                                    <span
                                      className="pf-version-released pf-version-released--client-approved"
                                      title={formatApprovalBadge(clientApproval) || 'Approved by the client in their portal'}
                                      aria-label={formatApprovalBadge(clientApproval) || 'Client approved'}
                                    >
                                      <CheckCircle2 size={11} aria-hidden />
                                      Client approved
                                    </span>
                                  ) : null}
                                  {!partnerReadOnly && latestHubRelease ? (
                                    <span
                                      className="pf-version-released pf-version-released--supplier"
                                      title={[
                                        `Staged for suppliers as v${latestHubRelease.releaseVersionIndex}`,
                                        formatDistanceToNow(latestHubRelease.createdAt, { addSuffix: true }),
                                        formatProposalLinkBadge(proposalRfqLinkFromRelease(latestHubRelease)),
                                      ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                      aria-label={`Staged for suppliers as v${latestHubRelease.releaseVersionIndex}`}
                                    >
                                      <CheckCircle2 size={11} aria-hidden />
                                      Staged v{latestHubRelease.releaseVersionIndex}
                                    </span>
                                  ) : null}
                                </div>
                                {(() => {
                                  const fileEdit = fileStructureEdit;
                                  if (!canSeeFileMoveAudit || !fileEdit || fileEdit.fileId !== vf.id) return null;
                                  return (
                                    <FileGroupPopover
                                      groups={structureGroups.map(group => ({
                                        id: group.structureId,
                                        label: group.structureName,
                                        detail: group.structureNames.length > 1 ? group.structureNames.join(' · ') : undefined,
                                      }))}
                                      value={fileEdit.groupIds}
                                      saving={fileStructureSaving}
                                      onChange={groupIds =>
                                        setFileStructureEdit(current => (current ? { ...current, groupIds } : current))
                                      }
                                      onSave={() => void commitFileStructureEdit()}
                                      onCancel={cancelFileStructureEdit}
                                    />
                                  );
                                })()}
                                <div className="pf-version-acts" role="toolbar" aria-label={`Actions for ${vf.name}`}>
                                  {versionOpensInline ? (
                                    <ProjectFileCommentsButton
                                      file={vf}
                                      commentCount={versionCommentCount}
                                      hasUnread={versionUnread}
                                      onOpen={openVersionInline}
                                    />
                                  ) : null}
                                  {isPdfType(vf.mimeType) ? (
                                    <>
                                    <button
                                      type="button"
                                      className="pf-version-act"
                                      onClick={openVersionInline}
                                    >
                                      Open
                                    </button>
                                    {streamVersions.filter(f => isPdfType(f.mimeType) && (f.id || f.url) !== (vf.id || vf.url)).length > 0 ? (
                                      <>
                                        <span className="pf-version-act-sep" aria-hidden>
                                          ·
                                        </span>
                                        <button
                                          type="button"
                                          className="pf-version-act"
                                          onClick={() => openVersionPdf(vf, { compare: true })}
                                          title="Open this drawing beside the previous version so you can review two revisions side by side"
                                        >
                                          Compare
                                        </button>
                                      </>
                                    ) : null}
                                    </>
                                  ) : isCsvType(vf.mimeType, vf.name) ? (
                                    <button
                                      type="button"
                                      className="pf-version-act"
                                      onClick={openVersionInline}
                                    >
                                      Open
                                    </button>
                                  ) : isHtmlType(vf.mimeType, vf.name) ? (
                                    <button
                                      type="button"
                                      className="pf-version-act"
                                      onClick={() => openHtmlViewer(vf)}
                                    >
                                      Open
                                    </button>
                                  ) : (
                                    <a
                                      className="pf-version-act"
                                      href={portalHostedAssetHref(vf.url, { downloadName: vf.name })}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Open
                                    </a>
                                  )}
                                  {isPdfType(vf.mimeType) &&
                                  (canStartPlanReview || activeVersionReview) ? (
                                    <>
                                      <span className="pf-version-act-sep" aria-hidden>
                                        ·
                                      </span>
                                      <button
                                        type="button"
                                        className={
                                          'pf-version-act pf-version-act--review'
                                          + (activeVersionReview ? ' pf-version-act--review-active' : '')
                                        }
                                        disabled={planReviewStartingId === vf.id}
                                        onClick={() => void handleReviewVersion(vf)}
                                        title={
                                          activeVersionReview
                                            ? 'Open drawing to continue or complete this plan review'
                                            : completedVersionReview
                                              ? 'Optional — start another quiet plan review on this version'
                                              : 'Optional — start a quiet plan review on this version'
                                        }
                                        aria-label={
                                          activeVersionReview
                                            ? `Continue plan review for ${vf.name}`
                                            : `Review ${vf.name}`
                                        }
                                      >
                                        {planReviewStartingId === vf.id ? (
                                          <Loader2
                                            size={12}
                                            className="pf-spin pf-version-act-spinner"
                                            aria-hidden
                                          />
                                        ) : (
                                          <>
                                            <ClipboardCheck size={12} aria-hidden />
                                            {activeVersionReview ? 'Continue review' : 'Review'}
                                          </>
                                        )}
                                      </button>
                                    </>
                                  ) : null}
                                  {canEditVersionLabel ? (
                                    <>
                                      <span className="pf-version-act-sep" aria-hidden>
                                        ·
                                      </span>
                                      <button
                                        type="button"
                                        className="pf-version-act pf-version-act--muted"
                                        onClick={() => beginVersionEdit(vf)}
                                        title="Edit the LOD and version name"
                                      >
                                        Edit
                                      </button>
                                    </>
                                  ) : null}
                                  <span className="pf-version-act-sep" aria-hidden>
                                    ·
                                  </span>
                                  <button
                                    type="button"
                                    className="pf-version-act pf-version-act--muted"
                                    onClick={() => handleDownload(vf)}
                                  >
                                    Download
                                  </button>
                                  {canRelease &&
                                    selectedStream &&
                                    isReleasableStreamKind(selectedStream.streamKind) &&
                                    portalId && (() => {
                                      const isReleased = !!latestRelease;
                                      return (
                                        <button
                                          type="button"
                                          className={
                                            'pf-version-act pf-version-act--release'
                                            + (isReleased ? ' pf-version-act--re-release' : '')
                                          }
                                          onClick={() => {
                                            if (!selectedStream.id || !vf.id) return;
                                            setReleaseModalState({
                                              streamId: selectedStream.id,
                                              streamKind: selectedStream.streamKind as ReleasableStreamKind,
                                              streamTitle: selectedStream.title,
                                              initialVersionId: vf.id,
                                            });
                                          }}
                                          aria-label={
                                            isReleased
                                              ? `Re-release ${vf.name} to the client portal (currently released as v${latestRelease!.releaseVersionIndex})`
                                              : `Release ${vf.name} to the client portal`
                                          }
                                          title={
                                            isReleased
                                              ? `Already released as v${latestRelease!.releaseVersionIndex} — click to push a new release version`
                                              : 'Release this version to the client portal'
                                          }
                                        >
                                          {isReleased ? (
                                            <>
                                              <RotateCw size={12} aria-hidden /> Re-release
                                            </>
                                          ) : (
                                            <>
                                              <Send size={12} aria-hidden /> Release
                                            </>
                                          )}
                                        </button>
                                      );
                                    })()}
                                  {canRelease &&
                                    selectedStream &&
                                    isReleasableStreamKind(selectedStream.streamKind) &&
                                    portalId &&
                                    latestRelease &&
                                    vf.id && (
                                      <button
                                        type="button"
                                        className="pf-version-act pf-version-act--unrelease"
                                        onClick={() =>
                                          handleUnrelease(
                                            vf,
                                            versionReleases || [latestRelease],
                                          )
                                        }
                                        disabled={unreleasingId === vf.id}
                                        aria-label={`Un-release ${vf.name} from the client portal`}
                                        title="Remove this version from the client portal"
                                      >
                                        {unreleasingId === vf.id ? (
                                          <Loader2
                                            size={12}
                                            className="pf-spin pf-version-act-spinner"
                                            aria-hidden
                                          />
                                        ) : (
                                          <>
                                            <Undo2 size={12} aria-hidden /> Un-release
                                          </>
                                        )}
                                      </button>
                                    )}
                                  {canReleaseToSuppliers &&
                                    selectedStream &&
                                    isRfqStagingReleasableStreamKind(selectedStream.streamKind) &&
                                    (() => {
                                      const isStaged = !!latestHubRelease;
                                      return (
                                        <button
                                          type="button"
                                          className={
                                            'pf-version-act pf-version-act--release'
                                            + (isStaged ? ' pf-version-act--re-release' : '')
                                          }
                                          onClick={() => {
                                            if (!selectedStream.id || !vf.id) return;
                                            setStagingReleaseModalState({
                                              streamId: selectedStream.id,
                                              streamKind: selectedStream.streamKind as RfqStagingReleasableStreamKind,
                                              streamTitle: selectedStream.title,
                                              initialVersionId: vf.id,
                                            });
                                          }}
                                          aria-label={
                                            isStaged
                                              ? `Re-stage ${vf.name} for suppliers (currently staged as v${latestHubRelease!.releaseVersionIndex})`
                                              : `Stage ${vf.name} for suppliers`
                                          }
                                          title={
                                            isStaged
                                              ? `Already staged as v${latestHubRelease!.releaseVersionIndex} — click to stage a new release version`
                                              : 'Stage this version for Supplier Portal'
                                          }
                                        >
                                          {isStaged ? (
                                            <>
                                              <RotateCw size={12} aria-hidden /> Re-stage
                                            </>
                                          ) : (
                                            <>
                                              <Send size={12} aria-hidden /> Stage
                                            </>
                                          )}
                                        </button>
                                      );
                                    })()}
                                  {canMutateFiles && !inherited && (
                                  <button
                                    type="button"
                                    className="pf-version-act pf-version-act--danger"
                                    onClick={() => handleDelete(vf)}
                                    disabled={deletingId === vf.id}
                                    aria-label={`Delete ${vf.name}`}
                                  >
                                    {deletingId === vf.id ? (
                                      <Loader2 size={12} className="pf-spin pf-version-act-spinner" aria-hidden />
                                    ) : (
                                      <>
                                        <Trash2 size={12} strokeWidth={2} aria-hidden /> Remove
                                      </>
                                    )}
                                  </button>
                                  )}
                                </div>
                              </div>
                              {versionEdit != null && versionEdit.fileId === vf.id ? (
                                <form
                                  className="pf-version-edit"
                                  onSubmit={event => {
                                    event.preventDefault();
                                    void commitVersionEdit();
                                  }}
                                >
                                  <label className="pf-version-edit-field">
                                    <span className="pf-vr-mu">LOD</span>
                                    <select
                                      className="pf-text-input pf-text-input-height"
                                      aria-label="Level of detail"
                                      value={versionEdit.detailStage}
                                      disabled={renameSaving}
                                      onChange={event =>
                                        setVersionEdit((current) =>
                                          current
                                            ? {
                                                ...current,
                                                detailStage: event.target.value as ProjectFileDetailStage | '',
                                              }
                                            : current,
                                        )
                                      }
                                    >
                                      <option value="">Ungrouped</option>
                                      {PROJECT_FILE_DETAIL_STAGES.map(stage => (
                                        <option key={stage} value={stage}>
                                          {PROJECT_FILE_DETAIL_STAGE_LABELS[stage]}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  {selectedStream?.streamKind === 'timber_list_csv' ? (
                                    <label className="pf-cnc-toggle">
                                      <input
                                        type="checkbox"
                                        checked={versionEdit.trueDimensions}
                                        disabled={renameSaving}
                                        onChange={event =>
                                          setVersionEdit((current) =>
                                            current ? { ...current, trueDimensions: event.target.checked } : current,
                                          )
                                        }
                                      />
                                      True dimensions
                                    </label>
                                  ) : null}
                                  {selectedStream?.streamKind === 'timber_list_csv' ? (
                                    <p className="pf-version-edit-hint">
                                      {versionEdit.trueDimensions
                                        ? 'Open this CSV to see height and width as written (7.5 stays 7.5). Length is unchanged. Save to keep it for quotes.'
                                        : 'Open this CSV to see height and width rounded to the nearest inch (7.5 becomes 8). Length is unchanged.'}
                                    </p>
                                  ) : null}
                                  <label className="pf-version-edit-field">
                                    <span className="pf-vr-mu">Label</span>
                                    <input
                                      ref={versionEditLabelRef}
                                      className="pf-text-input pf-text-input-height"
                                      value={versionEdit.label}
                                      disabled={renameSaving}
                                      aria-label="Version name"
                                      onChange={event =>
                                        setVersionEdit((current) =>
                                          current ? { ...current, label: event.target.value } : current,
                                        )
                                      }
                                    />
                                  </label>
                                  {selectedStream?.streamKind === 'three_d_raw' ? (
                                    <>
                                      <label className="pf-cnc-toggle">
                                        <input
                                          type="checkbox"
                                          checked={versionEdit.forCnc}
                                          disabled={renameSaving}
                                          onChange={event => {
                                            const checked = event.target.checked;
                                            // Split project: preselect this track's structures.
                                            const preselect =
                                              checked && structuresReady
                                                ? defaultCncStructureIdsForTrack(
                                                    selectedStream?.structureIds,
                                                    hubStructures.map(s => s.id),
                                                  )
                                                : [];
                                            setVersionEdit((current) =>
                                              current
                                                ? {
                                                    ...current,
                                                    forCnc: checked,
                                                    structureIds: checked
                                                      ? current.structureIds.length > 0
                                                        ? current.structureIds
                                                        : preselect
                                                      : [],
                                                  }
                                                : current,
                                            );
                                          }}
                                        />
                                        For CNC
                                      </label>
                                      {versionEdit.forCnc ? (
                                        !structuresReady ? (
                                          <p className="pf-cnc-empty">{STRUCTURES_LOCKED_HINT}</p>
                                        ) : (
                                          <StructureCheckDropdown
                                            compact
                                            hideClear
                                            aria-label="Structures this CNC version covers"
                                            placeholder="Pick structures…"
                                            structures={hubStructures}
                                            value={versionEdit.structureIds}
                                            disabled={renameSaving}
                                            onChange={structureIds =>
                                              setVersionEdit(current => (current ? { ...current, structureIds } : current))
                                            }
                                          />
                                        )
                                      ) : null}
                                    </>
                                  ) : null}
                                  <div className="pf-version-edit-actions">
                                    <button type="submit" className="pf-version-act" disabled={renameSaving}>
                                      {renameSaving ? 'Saving…' : 'Save'}
                                    </button>
                                    <button
                                      type="button"
                                      className="pf-version-act pf-version-act--muted"
                                      disabled={renameSaving}
                                      onClick={() => setVersionEdit(null)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </form>
                              ) : null}
                              <p className="pf-version-meta-line">
                                <span className="pf-version-file" title={vf.name}>
                                  {vf.name}
                                </span>
                                <span className="pf-version-meta-dot"> · </span>
                                <span>{formatDistanceToNow(vf.uploadedAt, { addSuffix: true })}</span>
                                {!singleSubmitterInTrack ? (
                                  <>
                                    <span className="pf-version-meta-dot"> · </span>
                                    <span className="pf-version-by">{vf.uploadedByName || 'Unknown'}</span>
                                  </>
                                ) : null}
                                {activeVersionReview ? (
                                  <>
                                    <span className="pf-version-meta-dot"> · </span>
                                    <span className="pf-version-review-meta">
                                      Review by {activeVersionReview.startedByName}
                                      {' · '}
                                      {formatDistanceToNow(activeVersionReview.startedAt, {
                                        addSuffix: true,
                                      })}
                                    </span>
                                  </>
                                ) : completedVersionReview ? (
                                  <>
                                    <span className="pf-version-meta-dot"> · </span>
                                    <span className="pf-version-review-meta">
                                      Reviewed by{' '}
                                      {completedVersionReview.completedByName ||
                                        completedVersionReview.startedByName}
                                      {completedVersionReview.completedAt ? (
                                        <>
                                          {' · '}
                                          {formatDistanceToNow(completedVersionReview.completedAt, {
                                            addSuffix: true,
                                          })}
                                        </>
                                      ) : null}
                                    </span>
                                  </>
                                ) : null}
                              </p>
                              {vf.versionNote ? (
                                <p className="pf-version-note pf-version-note--minimal">{vf.versionNote}</p>
                              ) : null}
                            </div>
                          </li>
                        );
                        };
                        const versionList = (files: ProjectFile[]) => (
                          <ul className="pf-version-timeline">{files.map(renderVersion)}</ul>
                        );
                        if (versionLayout === 'timeline' || versionStageTree.stages.length === 0) {
                          return versionList(streamVersions);
                        }
                        const streamBlocks: React.ReactNode[] = [];
                        let pendingFiles: ProjectFile[] = [];
                        const flushPending = () => {
                          if (pendingFiles.length === 0) return;
                          const batch = pendingFiles;
                          pendingFiles = [];
                          streamBlocks.push(
                            <ul key={batch.map(file => file.id).join('|')} className="pf-version-timeline">
                              {batch.map(renderVersion)}
                            </ul>,
                          );
                        };
                        for (const item of versionStageTree.stream) {
                          if (item.kind === 'file') {
                            pendingFiles.push(item.file);
                            continue;
                          }
                          flushPending();
                          const group = item.group;
                          const open = expandedStages.has(group.stage);
                          streamBlocks.push(
                            <section key={group.stage} className={`pf-version-stage pf-version-stage--${group.stage}`}>
                              <button
                                type="button"
                                className="pf-version-stage-toggle"
                                aria-expanded={open}
                                onClick={() => toggleVersionStage(group.stage)}
                              >
                                <ChevronRight size={14} aria-hidden />
                                <span>{group.label}</span>
                                <span className="pf-version-stage-count">{group.files.length}</span>
                              </button>
                              {open ? versionList(group.files) : null}
                            </section>,
                          );
                        }
                        flushPending();
                        return (
                          <div className="pf-version-stage-tree">
                            {streamBlocks}
                          </div>
                        );
                      })()
                    )}
                  </>
                ) : (
                  <div className="pf-timeline-empty">Select a row on the left to see its revisions.</div>
                )}
              </div>
            </div>
          )}


          {classicView && !partnerReadOnly && streams.length > 0 && filesNotInStreams.length > 0 && (
            <h3 className="pf-section-title pf-unversioned-heading">
              <FolderOpen size={14} /> Other project files
            </h3>
          )}

          {classicView && !partnerReadOnly && (
          <ProjectFilesUnversioned
            projectId={projectId}
            dealId={dealId}
            files={filesNotInStreams}
            onMoveToFolder={handleMoveToFolder}
            fileHasUnreadFileComment={fileHasUnreadFileComment}
            getFileCommentCount={getFileCommentCount}
            onOpenPdf={(file) =>
              setPdfViewerDoc({
                url: portalHostedAssetHref(file.url, { downloadName: file.name }),
                title: file.name,
                documentId: file.id || file.url,
                streamId: file.streamId,
                sharedAcrossStructures: fileIsSharedAcrossStructures(file),
              })
            }
            onOpenImage={(file) =>
              setImageViewerDoc({
                url: portalHostedAssetHref(file.url, { downloadName: file.name }),
                title: file.name,
                documentId: file.id || file.url,
                mimeType: file.mimeType,
                sharedAcrossStructures: fileIsSharedAcrossStructures(file),
              })
            }
            onOpenCsv={(file) =>
              setCsvViewerDoc({
                url: portalHostedAssetHref(file.url, { downloadName: file.name }),
                title: file.name,
                documentId: file.id || file.url,
                sharedAcrossStructures: fileIsSharedAcrossStructures(file),
                trueDimensions: viewerTrueDimensions(file),
              })
            }
            onOpenHtml={openHtmlViewer}
            onDownload={handleDownload}
            onDelete={handleDelete}
            deletingId={deletingId}
          />
          )}
        </div>
      )}


      {/* Drag overlay */}
      {canMutateFiles && dragOver && (
        <div className="pf-drop-overlay">
          <Upload size={32} />
          <span>
            {effectiveView === 'folders'
              ? 'Drop files here — they go to the selected project folder'
              : 'Drop files here — set version vs general in the upload dialog'}
          </span>
        </div>
      )}

      {/* Image viewer with annotations (same Firestore pins as PDFs, page 1) */}
      {imageViewerDoc && viewerLeadId && (
        <Suspense fallback={null}>
          <ImageViewerModal
            url={imageViewerDoc.url}
            title={imageViewerDoc.title}
            documentId={imageViewerDoc.documentId}
            leadId={viewerLeadId}
            leadName={leadName}
            projectId={projectId}
            dealId={dealId}
            mimeType={imageViewerDoc.mimeType}
            initialAnnotationId={imageViewerDoc.annotationId}
            sharedAcrossStructures={imageViewerDoc.sharedAcrossStructures}
            onClose={() => setImageViewerDoc(null)}
          />
        </Suspense>
      )}

      {/* CSV viewer with the same comment / draw / highlight model */}
      {csvViewerDoc && viewerLeadId && (
        <Suspense fallback={null}>
          <CsvViewerModal
            url={csvViewerDoc.url}
            title={csvViewerDoc.title}
            documentId={csvViewerDoc.documentId}
            leadId={viewerLeadId}
            leadName={leadName}
            projectId={projectId}
            dealId={dealId}
            initialAnnotationId={csvViewerDoc.annotationId}
            sharedAcrossStructures={csvViewerDoc.sharedAcrossStructures}
            trueDimensions={csvViewerDoc.trueDimensions === true}
            onClose={() => setCsvViewerDoc(null)}
          />
        </Suspense>
      )}

      {/* PDF viewer with annotations (suppliers may open without a CRM leadId) */}
      {pdfViewerDoc && (leadId || pdfViewerDoc.leadId || supplierReadOnly) && (
        <Suspense fallback={null}>
          <PdfViewerModal
            url={pdfViewerDoc.url}
            title={pdfViewerDoc.title}
            documentId={pdfViewerDoc.documentId}
            leadId={leadId || pdfViewerDoc.leadId || projectId}
            leadName={leadName}
            projectId={projectId}
            dealId={dealId}
            streamId={pdfViewerDoc.streamId}
            siblingVersions={pdfViewerDoc.siblingVersions}
            initialCompareDocumentId={pdfViewerDoc.initialCompareDocumentId}
            initialAnnotationId={pdfViewerDoc.annotationId}
            initialPageNumber={pdfViewerDoc.pageNumber}
            architectSurface={architectReadOnly}
            sharedAcrossStructures={pdfViewerDoc.sharedAcrossStructures}
            onClose={() => setPdfViewerDoc(null)}
          />
        </Suspense>
      )}

      {/* 3D HTML viewer popup */}
      {htmlViewerDoc && (
        <Suspense fallback={null}>
          <HtmlViewerModal
            url={htmlViewerDoc.url}
            fetchUrl={htmlViewerDoc.fetchUrl}
            title={htmlViewerDoc.title}
            versionLabel={htmlViewerDoc.versionLabel}
            documentId={htmlViewerDoc.documentId}
            projectId={htmlViewerDoc.projectId}
            projectName={htmlViewerDoc.projectName}
            streamId={htmlViewerDoc.streamId}
            siblingModels={htmlViewerDoc.siblingModels}
            sharedAcrossStructures={htmlViewerDoc.sharedAcrossStructures}
            onClose={() => setHtmlViewerDoc(null)}
          />
        </Suspense>
      )}

      {requestWizard && dealId ? (
        <RequestTimberQuotesWizard
          dealId={dealId}
          projectId={projectId}
          dealTitle={deal?.title || deal?.descriptor}
          timberProjectName={timberProjectName}
          streams={streams}
          files={files}
          structures={hubStructures}
          lanes={requestCoverageLanes}
          supplierOrgs={supplierOrgs}
          existingProject={timberRfqProject}
          cncProject={cncRfqProject}
          existingReleases={projectHubReleases}
          existingPackages={rfqPackages}
          existingRequests={rfqRequests}
          proposalNodeSets={proposalForRequests?.nodeSets ?? null}
          proposalSpeciesSource={proposalForRequests?.species ?? null}
          projectSite={projectSiteInputFromProjectAndDeal(timberProjectForSite, dealForSite)}
          initialStreamId={requestWizard.initialStreamId}
          initialStreamIds={requestWizard.initialStreamIds ?? null}
          initialReleaseIds={requestWizard.initialReleaseIds ?? null}
          busy={requestBusy}
          onCancel={() => {
            if (!requestBusy) setRequestWizard(null);
          }}
          onSend={draft => void sendQuoteRequest(draft)}
        />
      ) : null}
      {structuresModalOpen ? (
        <StructureSetupWizard
          streams={visibleStreams}
          files={files}
          structures={proposalStructures}
          versionCounts={splitVersionCounts}
          busy={structuresBusy}
          onCancel={() => {
            if (!structuresBusy) setStructuresModalOpen(false);
          }}
          onConfirm={plan => void confirmStructureSetup(plan)}
        />
      ) : null}
      {addStructureOpen ? (
        <AddStructureModal
          streams={streams}
          files={files}
          structures={proposalStructures}
          busy={structuresBusy}
          onCancel={() => {
            if (!structuresBusy) setAddStructureOpen(false);
          }}
          onConfirm={input => void confirmAddStructure(input)}
        />
      ) : null}
      {moveGroup && dealId ? (
        <MoveStructureGroupModal
          groupName={moveGroup.structureName}
          groupKey={moveGroup.structureId}
          sourceProjectId={projectId}
          sourceDealId={dealId}
          sourceLeadId={leadId}
          streams={streams}
          files={files}
          onClose={() => setMoveGroup(null)}
        />
      ) : null}
      {moveModalOpen && selectedStream ? (
        <MoveVersionsModal
          sourceStream={selectedStream}
          versions={streamVersions.filter(v => v.id && selectedVersionIds.has(v.id))}
          streams={streams}
          structures={hubStructures}
          structuresReady={structuresReady}
          portalHeadFileIds={portalHeadFileIds}
          busy={moveBusy}
          onCancel={() => {
            if (!moveBusy) setMoveModalOpen(false);
          }}
          onConfirm={target => void confirmMove(target)}
        />
      ) : null}
      {/* Release-to-client-portal modal (PM/admin only). */}
      {releaseModalState && portalId && dealId && (
        <ReleaseToClientPortalModal
          portalId={portalId}
          dealId={dealId}
          streamKind={releaseModalState.streamKind}
          streamTitle={releaseModalState.streamTitle}
          versions={
            selectedStreamId === releaseModalState.streamId ? streamVersions : []
          }
          existingReleases={
            selectedStreamId === releaseModalState.streamId ? streamReleases : []
          }
          deal={deal ?? null}
          initialVersionId={releaseModalState.initialVersionId}
          structureNames={
            streams.find(s => s.id === releaseModalState.streamId)?.structureNames ?? []
          }
          onClose={() => setReleaseModalState(null)}
          onReleased={result => {
            setReleaseToast(`Released ${result.name}`);
          }}
        />
      )}

      {vendorReleaseModalState && vendorRfqProjectId && vendorReleaseOrgId && dealId && (
        <ReleaseToVendorModal
          rfqProjectId={vendorRfqProjectId}
          supplierOrgId={vendorReleaseOrgId}
          dealId={dealId}
          streamTitle={vendorReleaseModalState.streamTitle}
          versions={
            selectedStreamId === vendorReleaseModalState.streamId ? streamVersions : []
          }
          initialVersionId={vendorReleaseModalState.initialVersionId}
          onClose={() => setVendorReleaseModalState(null)}
          onReleased={(result) => {
            setReleaseToast(`Released to vendor: ${result.name}`);
          }}
        />
      )}

      {stagingReleaseModalState && dealId && (
        <ReleaseToRfqStagingModal
          dealId={dealId}
          timberProjectId={projectId}
          timberProjectName={timberProjectName}
          dealTitle={deal?.title || deal?.descriptor}
          streamKind={stagingReleaseModalState.streamKind}
          streamTitle={stagingReleaseModalState.streamTitle}
          structureNames={streams.find(s => s.id === stagingReleaseModalState.streamId)?.structureNames}
          structureIds={streams.find(s => s.id === stagingReleaseModalState.streamId)?.structureIds}
          proposalStructures={hubStructures}
          versions={
            selectedStreamId === stagingReleaseModalState.streamId ? streamVersions : []
          }
          initialVersionId={stagingReleaseModalState.initialVersionId}
          onClose={() => setStagingReleaseModalState(null)}
          onReleased={(result) => {
            setReleaseToast(`Staged for suppliers: ${result.name}`);
          }}
        />
      )}

      {/* Subtle toast confirming a release. Auto-dismisses; uses Lucide CheckCircle2 (no emojis). */}
      {releaseToast && (
        <div className="pf-release-toast" role="status" aria-live="polite">
          <CheckCircle2 size={14} aria-hidden /> {releaseToast}
        </div>
      )}
    </div>
  );
};

export default ProjectFiles;
